import { AppDispatch, RootState } from "../index";
import {
  addTicketActivityComment,
  addTicketNote,
  EditableTicketInfo,
  MyTicketRow,
  SustainQueueRow,
  type AccessRequestDetail,
  type AccessRequestLocationDetail,
  NoteItem,
  ActivityItem,
  setLoading,
  setData,
  setDetailLoading,
  setError,
  setMyTickets,
  setMyTicketsLoading,
  setMyTicketsTotalElements,
  setClosedTicketsCount,
  setMyTicketsError,
  setSustainQueue,
  setSustainQueueLoading,
  setSustainQueueTotalElements,
  setSustainQueueCurrentPage,
  setSelectedTicketId,
  setTicketNotes,
  TicketingPortalData,
  TicketDetailRecord,
  upsertTicketDetail,
  updateTicketDetailInfo,
  TicketStatus,
  TicketUrgency,
  setSubmitTicketFormConfigSuccess,
  DashboardCounts,
  setDashboardCounts,
  setDashboardCountsLoading,
  setSustainTeamMembers,
  setSustainTeamMembersLoading,
  setSustainTeamMembersError,
  SustainTeamApiMember,
  setAllLocations,
  LocationItem,
  setHomepageBanners,
  setNotificationsApiCache,
  TicketingNotificationApiItem,
  setTicketingTeams,
  setAvailableTicketingTeams,
} from "../slices/ticketingPortalSlice";
import { refetchNotifications } from "../slices/notificationSlice";
import { logger } from "../../utils/logger";
import { reloadUserProfile } from "./userProfileThunk";
import { reportError } from "../../utils/Instrumentation";
import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from "../../api/apiClient";
import { getCategoryBetaKey, getCategoryBetaParams, isCategoryConfigStale } from "../../utils/ticketingCategoryBeta";
import { formatUserName } from "../../utils/emailTouserName";
import { formatDateWithTimezone } from "../../screens/TicketingPortal/util/dateFormatter";
import { locationApi } from "../../screens/Admin/pages/LocationManagement/locationApi";
import { DEFAULT_FILTERS } from "../../screens/Admin/pages/LocationManagement/locationTypes";
import axios from "axios";
import { ticketingConfigApi } from "../../api/ticketingConfigApi";
import { getCategoryRequestContext, selectIsCategoryConfigStale } from "../selectors/appDataSelectors";

/**
 * Checks if a ticket is still within the 24-hour duplicate detection window.
 * Used to auto-expire duplicate badges when they're older than 24 hours.
 */
function isWithin24HourDuplicateWindow(submittedAt: string): boolean {
  const submitted = new Date(submittedAt).getTime();
  const now = Date.now();
  const HOURS_24_MS = 24 * 60 * 60 * 1000;
  return (now - submitted) <= HOURS_24_MS;
}

const getClientTimeQueryParams = () => ({
  clientTimestamp: getLocalTimestamp(),
  clientTimezone: getTimezoneOffsetString(),
});

export const fetchTicketingTeams = () => async (dispatch: AppDispatch): Promise<void> => {
  const teams = await ticketingConfigApi.getTeams();
  dispatch(setTicketingTeams(teams));
};

export const fetchAvailableTicketingTeams = () => async (dispatch: AppDispatch): Promise<void> => {
  const teams = await ticketingConfigApi.getTeams({ excludeWithoutOpenTickets: true });
  dispatch(setAvailableTicketingTeams(teams));
};

const normalizeAccessRequestLocation = (location: any): AccessRequestLocationDetail => {
  const locationType = String(location?.locationType ?? "").trim().toUpperCase();
  const normalizedLocationType: AccessRequestLocationDetail["locationType"] =
    locationType === "MARKET" || locationType === "REGION" ? locationType : "LOCATION";
  const normalizedName = String(location?.locationName ?? location?.locName ?? location?.name ?? "").trim() || null;
  const normalizedId = String(location?.locationId ?? location?.locId ?? "").trim() || null;
  const normalizedMarket = String(location?.market ?? location?.marketName ?? "").trim() || null;
  const normalizedRegion = String(location?.region ?? location?.regionName ?? "").trim() || null;

  return {
    locationType: normalizedLocationType,
    locationId: normalizedLocationType === "LOCATION" ? normalizedId : null,
    locationName: normalizedLocationType === "LOCATION" ? normalizedName : null,
    // Preserve any region/market values present on the incoming location object so
    // downstream UI (grouping & tree rendering) can display the correct geography
    // even for LOCATION-scoped entries returned by the API.
    region: normalizedRegion || (normalizedLocationType === "REGION" ? normalizedRegion ?? normalizedName : null),
    market: normalizedMarket || (normalizedLocationType === "MARKET" ? normalizedMarket ?? normalizedName : null),
  };
};

const normalizeAccessRequestDetail = (accessRequest: any): AccessRequestDetail | undefined => {
  if (!accessRequest) {
    return undefined;
  }

  return {
    requestedForEmail: accessRequest.requestedForEmail,
    requestedForGpid: accessRequest.requestedForGpid,
    requestedForName: accessRequest.requestedForName,
    accessType: accessRequest.accessType,
    organization: accessRequest.organization,
    department: accessRequest.department,
    role: accessRequest.role,
    currentRole: accessRequest.currentRole ?? null,
    justification: accessRequest.justification,
    endDate: accessRequest.endDate,
    locations: Array.isArray(accessRequest.locations)
      ? accessRequest.locations.map(normalizeAccessRequestLocation)
      : undefined,
  };
};

/**
 * Normalizes resolution path values coming from the backend into the
 * display labels used by the frontend.
 *
 * The backend may return slugs (e.g. "internal-resolution", "requires-it")
 * or already-formatted labels. This maps both forms to the canonical display
 * labels expected by TriageCard ("Requires IT" / "Internal Resolution").
 */
const normalizeResolutionPath = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const lower = value.toLowerCase().trim();
  if (lower === "requires it" || lower === "requires-it") return "Requires IT";
  if (lower === "internal resolution" || lower === "internal-resolution") return "Internal Resolution";
  // Return the value as-is if it doesn't match a known slug (preserves future values)
  return value;
};




const MOCK_TICKET_DETAIL: TicketDetailRecord = {
  id: "NTI-2026-00421",
  title: "EWM login failure blocking outbound picks",
  description:
    'Starting around 6:30 AM PT, pickers at multiple DCs in the SoCal market began receiving "Authentication failed" errors when logging into EWM on their RF devices. The login screen itself loads fine, but credentials (verified working on the corporate portal) are rejected. Impact: outbound pick waves are paused. We have a manual slip picklist workaround in place but throughput is down significantly. No recent changes we\'re aware of on our side.',
  category: "System Issue",
  subcategory: "EWM / SAP",
  location: "SoCal (Market)",
  sapId: "1000",
  urgency: "critical",
  status: "progress",
  submitted: "04/01/2026 6:42 AM PT",
  lastUpdated: "04/01/2026 9:14 AM PT",
  submittedBy: "Alex Rivera",
  market: "SoCal Market (SAP: 1000)",
  ticketLevel: "Market",
  peopleImpacted: "~180 pickers across shifts",
  loadsImpacted: "76 - 100%",
  stakeholders: "dhatton@pepsico.com, jmoreno@pepsico.com",
  incidentId: "INC-4820739",
  resolutionPath: "Requires IT",
  assignedTo: "Maria Chen",
  isWidespread: false,
  affectedLocations: [
    { id: "1", name: "Fullerton DC", sapId: "1234", time: "6:42 AM", isSubmitter: true },
    { id: "2", name: "Bondurant DC", sapId: "1245", time: "6:48 AM" },
    { id: "3", name: "Placentia DC", sapId: "1256", time: "6:51 AM" },
    { id: "4", name: "Riverside DC", sapId: "1267", time: "6:55 AM" },
    { id: "5", name: "Ontario DC", sapId: "1278", time: "7:02 AM" },
    { id: "6", name: "San Bernardino DC", sapId: "1289", time: "7:08 AM" },
    { id: "7", name: "Irvine DC", sapId: "1290", time: "7:14 AM" },
    { id: "8", name: "Anaheim DC", sapId: "1301", time: "7:22 AM" },
    { id: "9", name: "Long Beach DC", sapId: "1312", time: "7:31 AM" },
    { id: "10", name: "Torrance DC", sapId: "1323", time: "7:40 AM" },
  ],
  notes: [
    {
      id: "n1",
      authorInitials: "MC",
      authorName: "Maria Chen",
      role: "Sustain Team Lead",
      time: "04/01z 10:22 AM",
      body: "SAP Basis team confirmed cert propagation is running. ETA 11 AM PT. If not resolved by 11:15, escalate to Peter Lam directly.",
      avatarColor: "#7c3aed",
    },
    {
      id: "n2",
      authorInitials: "DP",
      authorName: "Derek Park",
      role: "Sustain Specialist",
      time: "04/17 8:15 AM",
      body: "Pulled the past 30 days of auth error rates from Splunk. Not seeing any prior elevation, so this is a clean break not a slow degrade.",
      avatarColor: "#0d9488",
    },
  ],
  activity: [
    {
      id: "a1",
      authorInitials: "AR",
      authorName: "Alex Rivera",
      role: "Submitter",
      time: "04/17 6:42 AM",
      content: "Submitted ticket. Please see the attached screenshot of the error message.",
      type: "user",
    },
    {
      id: "a2",
      authorInitials: "SN",
      authorName: "ServiceNow",
      role: "Integration",
      time: "04/17 7:01 AM",
      content: "INC-4820739 created and linked. Status: New -> In Progress. Assigned to Corporate IT · SAP Basis.",
      type: "servicenow",
    },
  ],
};

/**
 * Thunk to fetch ticketing portal data
 * Can be dispatched from anywhere in the app
 */
export const fetchTicketingData = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));

    // TODO: Replace mock with real API when backend is ready
    const mockData: TicketingPortalData = {
      submitTicket: {
        avgTime: "2 min",
      },
      myTickets: {
        total: 0,
        needsAction: 1,
      },
      landing: {
        greeting: "", // derived from auth state in useTicketingPortalLandingModel
        subtitle:
          "Submit requests, track their progress, and stay informed about warehouse-wide issues.",
        incident: {
          active: false,
          title: "Widespread Incident: EWM Authentication Outage (NTI-2026-00421)",
          message:
            "14 locations reporting SAP authentication service unavailable. Workaround: manual slip picklist. ETA for resolution: 11:00 AM PT.",
          primaryActionLabel: "I'm Affected Too",
          secondaryActionLabel: "View",
        },
        kpis: [
          {
            label: "Unassigned",
            value: 5,
            deltaText: "+2 from yesterday",
            tone: "positive",
          },
          {
            label: "Critical Open",
            value: 3,
            deltaText: "No change",
            tone: "critical",
          },
          {
            label: "Waiting on User",
            value: 4,
            deltaText: "-1 from yesterday",
            tone: "warning",
          },
          {
            label: "Closed This Week",
            value: 42,
            deltaText: "+18% vs last week",
            tone: "positive",
          },
        ],
        actions: [
          {
            id: "submit-ticket",
            title: "Submit Ticket",
            description:
              "Report an issue or request support for your site. PepGPT can help triage before you submit.",
            metric: "Avg 2 min",
            icon: "note-add",
            iconBackgroundColor: "#dce8fb",
            iconColor: "#156fd5",
          },
          {
            id: "my-tickets",
            title: "My Tickets",
            description:
              "Track status and progress of tickets you've submitted or are a stakeholder on.",
            metric: "1 needs action",
            icon: "confirmation-number",
            iconBackgroundColor: "#d9ecf8",
            iconColor: "#0f87c5",
          },
          {
            id: "sustain-queue",
            title: "Sustain Queue",
            description:
              "Triage incoming requests, assign ownership, and resolve across the portfolio.",
            metric: "28 active",
            icon: "grid-view",
            iconBackgroundColor: "#e4e0fd",
            iconColor: "#7a71e0",
          },
          {
            id: "notifications-comms",
            title: "Notifications & Comms",
            description:
              "Create targeted notifications, manage homepage banners, and measure delivery.",
            metric: "3 active",
            icon: "notifications-none",
            iconBackgroundColor: "#fbead8",
            iconColor: "#d0781d",
          },
          {
            id: "team-management",
            title: "Team Management",
            description:
              "Manage Sustain Team members, roles, and notification preferences.",
            metric: "8 members",
            icon: "groups",
            iconBackgroundColor: "#dff1de",
            iconColor: "#36a259",
          },
        ],
      },
    };

    await new Promise((resolve) => setTimeout(resolve, 500));

    logger.log("Ticketing Portal Data (mock):", mockData);
    dispatch(setData(mockData));
    // Fetch live dashboard counts from Apigee in parallel (non-blocking)
    dispatch(fetchDashboardCounts());
    dispatch(setLoading(false));
  } catch (err: any) {
    console.error("Error fetching Ticketing Portal data:", err);
    const errorMessage = err.message || "Failed to load ticketing data";
    dispatch(setError(errorMessage));

    reportError({
      severity: "ERROR",
      message: errorMessage,
      stackTrace: err instanceof Error ? err.stack : undefined,
    });
  }
};

/**
 * Fetches live KPI counts from the Apigee-routed endpoint:
 *   GET /ticketing/dashboard/counts
 * Non-fatal — failure logs a warning but does not block the dashboard.
 */
export const fetchDashboardCounts = () => async (dispatch: AppDispatch): Promise<void> => {
  try {
    dispatch(setDashboardCountsLoading(true));
    const api = createApiClient();
    const response = await api.get<DashboardCounts>("/ticketing/dashboard/counts");
    dispatch(setDashboardCounts(response.data));
    logger.log("Dashboard counts loaded:", response.data);
  } catch (err: any) {
    dispatch(setDashboardCountsLoading(false));
    logger.warn("Failed to fetch dashboard counts:", err?.message);
    reportError({
      severity: "WARN",
      message: `fetchDashboardCounts failed: ${err?.message ?? "unknown error"}`,
      stackTrace: err instanceof Error ? err.stack : undefined,
    });
  }
};

const TICKET_STATUS_MAP: Record<string, TicketStatus> = {
  New: "new",
  "In Progress": "progress",
  "Escalated to IT": "waiting-it",
  "Waiting on IT": "waiting-it",
  "Waiting on It": "waiting-it",
  "Escalated to TMV": "waiting-tmv",
  "Waiting on TMV": "waiting-tmv",
  "Submitted to ADO": "submitted-ado",
  "Waiting on User": "waiting-user",
  "Waiting on Vendor": "waiting-vendor",
  Closed: "resolved",
  Resolved: "resolved",
};

const TICKET_URGENCY_MAP: Record<string, TicketUrgency> = {
  Critical: "critical",
  High: "high",
  Medium: "medium",
  Low: "low",
  Informational: "informational",
};

type TicketingAttachmentResponse = {
  id: number;
  fileName: string;
  storageUrl: string;
  fileSize: number | string;
};

type TicketingDynamicValueResponse = {
  fieldConfigId: number;
  historicalLabel: string;
  value: string;
  fieldNoLongerActive: boolean;
};

type TicketingAffectedLocationResponse = {
  locName: string;
  locId: string;
  sapId?: string | null;
  reportedTime?: string | null;
  isSubmitter?: boolean;
};

type TicketDetailApiResponse = {
  id: number;
  ticketNumber: string;
  submitterEmail: string;
  stakeholderEmails: string[];
  categoryId: string;
  subCategoryId: string;
  /** Optional third-level tertiary category id persisted with the ticket. */
  tertiaryId?: number | null;
  title: string;
  issueDescription: string;
  ticketLevel: string;
  levelReferenceId: string;
  urgency: string;
  status: string;
  resolutionPath?: string | null;
  assignedTo?: string | null;
  incNumber?: string | null;
  createdAt: string;
  modifiedAt?: string | null;
  closedAt?: string | null;
  reopenedFromId?: string;
  /** Total number of people (pickers) impacted by this ticket */
  totalImpactedPickers?: number;
  absorbedTicketIds?: string[];
  /** Backend field is singular "Ticket" (not "Tickets") */
  mergedTicketDetails?: Array<{ ticketNumber: string; title: string }>;
  /** Backend field is singular "Ticket" (not "Tickets") */
  mergedTicketDetails?: Array<{ ticketNumber: string; title: string }>;
  affectedLocations?: Array<string | TicketingAffectedLocationResponse>;
  dynamicValues?: TicketingDynamicValueResponse[];
  attachments?: TicketingAttachmentResponse[];
  /** ADO work item ID returned by backend when linked */
  adoWorkItemId?: number | null;
  /** Whether this ticket has duplicate tickets */
  hasDuplicate?: boolean;
  /** Count of duplicate tickets */
  duplicateCount?: number;
  accessRequest?: {
    requestedForEmail?: string;
    requestedForGpid?: string;
    requestedForName?: string;
    accessType: string;
    organization: string;
    department: string;
    role: string;
    currentRole?: string | null;
    justification?: string;
    endDate?: string;
    locations?: Array<{
      locationType: "LOCATION" | "MARKET" | "REGION";
      locationId: string;
      locationName: string;
      region?: string | null;
      market?: string | null;
      site?: string | null;
    }>;
  };
  /** Whether this ticket is marked as widespread */
  isWidespread?: boolean | null;
  /** Numeric ID of the primary ticket this ticket was merged into (absorbed tickets only) */
  mergedIntoTicketId?: number | null;
  /** Human-readable ticket number of the primary ticket (e.g. "NTI-2026-00366") */
  parentTicketRef?: string | null;
  /** Whether this ticket is related to Make, Move, Sell (MMS) toggle */
  mmsToggle?: boolean | null;
  /** RMA tickets: the vendor declined at least one unit on inspection */
  hasDeclinedUnit?: boolean | null;
  /** RMA tickets: negligent damage was declared at submission */
  negligentDamageNotice?: boolean | null;
  /** TKT_Teams.id the ticket is routed to (auto-resolved from Team Assignment Configuration, or manually reassigned); null if unresolved */
  assignedTeamId?: number | null;
};

type TicketActivityApiResponse = {
  id: number;
  ticketId: number;
  actorName: string;
  actorRoleTag?: string | null;
  activityType: string;
  activityText: string;
  /** Java backend may serialize as `serviceNow` (Jackson strips `is` prefix from boolean getters) */
  isServiceNow?: boolean;
  serviceNow?: boolean;
  createdAt: string;
  createdByUserId?: string | null;
  activityImageResponse?: string | null;
};

const getInitials = (name: string): string => {
  const normalized = String(name || "").trim();
  if (!normalized) return "NA";
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const inferActivityType = (item: TicketActivityApiResponse): ActivityItem["type"] => {
  // Cover Jackson-serialized `serviceNow` (boolean getter strips `is` prefix) and explicit `isServiceNow`
  if (item.isServiceNow || item.serviceNow) return "servicenow";
  const role = String(item.actorRoleTag || "").toLowerCase();
  const type = String(item.activityType || "").toLowerCase();
  const name = String(item.actorName || "").toLowerCase();
  if (type === "servicenow" || name === "servicenow") return "servicenow";
  if (role.includes("sustain")) return "sustain";
  if (type.includes("system") || type.includes("integration") || type.includes("status") || type.includes("notification")) {
    return "system";
  }
  return "user";
};

const mapApiActivityToItem = (item: TicketActivityApiResponse): ActivityItem => {
  const imagesFromResponse = parseNoteImageResponse(item.activityImageResponse);
  return {
    id: `a${item.id}`,
    authorInitials: getInitials(item.actorName),
    authorName: item.actorName || "Unknown",
    role: item.actorRoleTag || "",
    time: formatDateWithTimezone(item.createdAt),
    content: item.activityText,
    type: inferActivityType(item),
    ...(imagesFromResponse.length > 0 ? { images: imagesFromResponse } : {}),
  };
};

const fetchTicketActivitiesFromApi = async (
  api: ReturnType<typeof createApiClient>,
  backendTicketId: number,
): Promise<ActivityItem[]> => {
  try {
    const response = await api.get<TicketActivityApiResponse[]>(`/ticketing/activities/ticket/${backendTicketId}`);
    if (!Array.isArray(response.data)) {
      return [];
    }
    return response.data.map(mapApiActivityToItem);
  } catch {
    return [];
  }
};

const parseNoteImageResponse = (imageResponse: unknown): string[] => {
  if (imageResponse == null) {
    return [];
  }

  let parsed: unknown = imageResponse;
  if (typeof imageResponse === "string") {
    const trimmed = imageResponse.trim();
    if (!trimmed) {
      return [];
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return [];
  }

  return Object.entries(parsed as Record<string, unknown>)
    .filter(([, value]) => typeof value === "string" && value.trim().length > 0)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, value]) => String(value).trim());
};

const inferUploadMimeType = (imageUri: string): string => {
  const dataUriMatch = imageUri.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/i);
  if (dataUriMatch?.[1]) {
    return dataUriMatch[1].toLowerCase();
  }

  const lower = imageUri.toLowerCase();
  if (lower.includes(".png")) return "image/png";
  if (lower.includes(".gif")) return "image/gif";
  if (lower.includes(".jpg") || lower.includes(".jpeg")) return "image/jpeg";
  return "image/jpeg";
};

const inferUploadExtension = (mimeType: string): string => {
  if (mimeType === "image/png") return "png";
  if (mimeType === "image/gif") return "gif";
  return "jpg";
};

/**
 * Infers MIME type for a file URI/data URI, supporting images and documents.
 */
const inferFileMimeType = (fileUri: string, originalFileName?: string): string => {
  // Check data URI first
  const dataUriMatch = fileUri.match(/^data:([a-zA-Z0-9.+/-]+);/i);
  if (dataUriMatch?.[1]) return dataUriMatch[1].toLowerCase();

  // Check by extension from original file name or URI
  const nameToCheck = (originalFileName || fileUri.split("?")[0]).toLowerCase();
  if (nameToCheck.endsWith(".pdf")) return "application/pdf";
  if (nameToCheck.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (nameToCheck.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (nameToCheck.endsWith(".png")) return "image/png";
  if (nameToCheck.endsWith(".gif")) return "image/gif";
  if (nameToCheck.endsWith(".jpg") || nameToCheck.endsWith(".jpeg")) return "image/jpeg";
  return "application/octet-stream";
};

/**
 * Infers the file extension from a MIME type.
 */
const inferFileExtension = (mimeType: string, originalFileName?: string): string => {
  if (originalFileName) {
    const parts = originalFileName.split(".");
    if (parts.length > 1) return parts[parts.length - 1].toLowerCase();
  }
  if (mimeType === "image/png") return "png";
  if (mimeType === "image/gif") return "gif";
  if (mimeType === "application/pdf") return "pdf";
  if (mimeType.includes("spreadsheetml")) return "xlsx";
  if (mimeType.includes("wordprocessingml")) return "docx";
  return "jpg";
};

type NoteImageUploadResult = { fileNames: string[] };

/**
 * Uploads a single image to /ticketing/images/upload/image and returns the
 * server-assigned fileName from the response. Call this immediately when the
 * user picks an image so the upload happens before the submit action.
 */
export type TicketImageUploadResult = { fileName: string; imageUrl: string };

export const uploadTicketImage = async (
  api: ReturnType<typeof createApiClient>,
  imageUri: string,
  prefix: "ticket-note" | "ticket-comment" = "ticket-note",
): Promise<TicketImageUploadResult | null> => {
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
  const mimeType = inferUploadMimeType(imageUri);
  const ext = inferUploadExtension(mimeType);
  const fileName = `${prefix}-${Date.now()}.${ext}`;

  const isWebImageUri = imageUri.startsWith("data:") || imageUri.startsWith("blob:");

  if (isWebImageUri) {
    const blob = await fetch(imageUri).then((r) => r.blob());
    if (blob.size > MAX_FILE_SIZE_BYTES) return null;
    const formData = new FormData();
    formData.append("image", blob, fileName);
    const response = await api.post("/ticketing/images/upload/image", formData, {
      headers: { Accept: "application/json" },
    });
    const uploadedFileName: string | null = response?.data?.data?.fileName ?? null;
    const uploadedImageUrl: string | null = response?.data?.data?.imageUrl ?? null;
    if (typeof uploadedFileName === "string" && uploadedFileName.trim().length > 0
      && typeof uploadedImageUrl === "string" && uploadedImageUrl.trim().length > 0) {
      return { fileName: uploadedFileName.trim(), imageUrl: uploadedImageUrl.trim() };
    }
    return null;
  } else {
    const formData = new FormData();
    formData.append("image", {
      uri: imageUri,
      type: mimeType,
      name: fileName,
    } as any);
    const response = await api.post("/ticketing/images/upload/image", formData, {
      headers: { Accept: "application/json" },
    });
    const uploadedFileName: string | null = response?.data?.data?.fileName ?? null;
    const uploadedImageUrl: string | null = response?.data?.data?.imageUrl ?? null;
    if (typeof uploadedFileName === "string" && uploadedFileName.trim().length > 0
      && typeof uploadedImageUrl === "string" && uploadedImageUrl.trim().length > 0) {
      return { fileName: uploadedFileName.trim(), imageUrl: uploadedImageUrl.trim() };
    }
    return null;
  }
};

/**
 * Uploads a single file (image or document) to /ticketing/images/upload/file and returns the
 * server-assigned fileName and fileUrl from the response.
 * Supported: .jpg, .jpeg, .png, .pdf, .xlsx, .docx
 */
export type TicketFileUploadResult = { fileName: string; fileUrl: string };

export const uploadTicketFile = async (
  api: ReturnType<typeof createApiClient>,
  fileUri: string,
  originalFileName: string,
  prefix: "ticket-note" | "ticket-comment" | "ticket-document" = "ticket-note",
): Promise<TicketFileUploadResult | null> => {
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
  const mimeType = inferFileMimeType(fileUri, originalFileName);
  const ext = inferFileExtension(mimeType, originalFileName);
  const serverFileName = `${prefix}-${Date.now()}.${ext}`;

  const isWebUri = fileUri.startsWith("data:") || fileUri.startsWith("blob:");

  if (isWebUri) {
    const blob = await fetch(fileUri).then((r) => r.blob());
    if (blob.size > MAX_FILE_SIZE_BYTES) return null;
    const formData = new FormData();
    formData.append("file", blob, serverFileName);
    const response = await api.post("/ticketing/images/upload/file", formData, {
      headers: { Accept: "application/json" },
    });
    const uploadedFileName: string | null = response?.data?.data?.fileName ?? null;
    const uploadedFileUrl: string | null =
      response?.data?.data?.fileUrl ?? response?.data?.data?.imageUrl ?? null;
    if (typeof uploadedFileName === "string" && uploadedFileName.trim().length > 0
      && typeof uploadedFileUrl === "string" && uploadedFileUrl.trim().length > 0) {
      return { fileName: uploadedFileName.trim(), fileUrl: uploadedFileUrl.trim() };
    }
    return null;
  } else {
    const formData = new FormData();
    formData.append("file", {
      uri: fileUri,
      type: mimeType,
      name: serverFileName,
    } as any);
    const response = await api.post("/ticketing/images/upload/file", formData, {
      headers: { Accept: "application/json" },
    });
    const uploadedFileName: string | null = response?.data?.data?.fileName ?? null;
    const uploadedFileUrl: string | null =
      response?.data?.data?.fileUrl ?? response?.data?.data?.imageUrl ?? null;
    if (typeof uploadedFileName === "string" && uploadedFileName.trim().length > 0
      && typeof uploadedFileUrl === "string" && uploadedFileUrl.trim().length > 0) {
      return { fileName: uploadedFileName.trim(), fileUrl: uploadedFileUrl.trim() };
    }
    return null;
  }
};

const uploadNoteImages = async (
  api: ReturnType<typeof createApiClient>,
  images: string[],
): Promise<NoteImageUploadResult> => {
  if (images.length === 0) {
    return { fileNames: [] };
  }

  const MAX_FILE_SIZE_MB = 10;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const uploadedFileNames: string[] = [];
  const skippedFiles: string[] = [];

  for (let index = 0; index < images.length; index += 1) {
    const imageUri = images[index];
    const mimeType = inferUploadMimeType(imageUri);
    const fileName = `ticket-note-${Date.now()}-${index}.${inferUploadExtension(mimeType)}`;

    let fileSizeBytes = 0;
    const isWebImageUri = imageUri.startsWith("data:") || imageUri.startsWith("blob:");

    if (isWebImageUri) {
      // Web upload path: get blob size
      const blob = await fetch(imageUri).then((r) => r.blob());
      fileSizeBytes = blob.size;

      if (fileSizeBytes > MAX_FILE_SIZE_BYTES) {
        skippedFiles.push(`${fileName} (${(fileSizeBytes / (1024 * 1024)).toFixed(2)} MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append("image", blob, fileName);

      const response = await api.post("/admin/images/upload", formData, {
        headers: { Accept: "application/json" },
      });

      const uploadedFileName: string | null = response?.data?.data?.fileName ?? null;

      if (typeof uploadedFileName === "string" && uploadedFileName.trim().length > 0) {
        uploadedFileNames.push(uploadedFileName.trim());
      }
    } else {
      // Native upload path: estimate size from base64 or file size
      if (imageUri.startsWith("data:")) {
        const base64String = imageUri.split(",")[1] || "";
        fileSizeBytes = Math.ceil(base64String.length * 0.75);
      }

      if (fileSizeBytes > MAX_FILE_SIZE_BYTES) {
        skippedFiles.push(`${fileName} (${(fileSizeBytes / (1024 * 1024)).toFixed(2)} MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append("image", {
        uri: imageUri,
        type: mimeType,
        name: fileName,
      } as any);

      const response = await api.post("/admin/images/upload", formData, {
        headers: { Accept: "application/json" },
      });

      const uploadedFileName: string | null = response?.data?.data?.fileName ?? null;

      if (typeof uploadedFileName === "string" && uploadedFileName.trim().length > 0) {
        uploadedFileNames.push(uploadedFileName.trim());
      }
    }
  }

  if (skippedFiles.length > 0) {
    console.warn(`Files skipped due to exceeding ${MAX_FILE_SIZE_MB} MB limit:`, skippedFiles);
  }

  return { fileNames: uploadedFileNames };
};

const mapApiNoteToItem = (note: any): NoteItem => {
  const authorName = formatUserName(String(note.createdBy || "")) || String(note.createdBy || "Unknown").split("@")[0] || "Unknown";
  const imagesFromResponse = parseNoteImageResponse(note.imageResponse);

  return {
    id: `n${note.noteId}`,
    authorInitials: getInitials(authorName || "NA"),
    authorName,
    role: "Sustain Team",
    time: formatDateWithTimezone(note.createdAt),
    body: String(note.commentText ?? "").trim(),
    avatarColor: "#7c3aed",
    ...(imagesFromResponse.length > 0 ? { images: imagesFromResponse } : {}),
  };
};

export interface FetchMyTicketsParams {
  search?: string;
  /** API-format status string, e.g. "New", "In Progress" */
  status?: string;
  /** API-format urgency string, e.g. "High", "Medium". Literal string "null" = filter for tickets with no urgency. */
  urgency?: string;
  categoryId?: number;
  subCategoryId?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  pageNumber?: number;
  pageSize?: number;
  /** Email of the submitter — filters results to tickets submitted by this user. */
  submitterEmail?: string;
  /** When true, skips dispatching to Redux (used for fetching filter option data separately). */
  forFiltersOnly?: boolean;
}

export const fetchMyTickets = (params?: FetchMyTicketsParams) => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    if (!params?.forFiltersOnly) {
      dispatch(setMyTicketsLoading(true));
    }
    const api = createApiClient();
    const state = getState();
    const authUser = state.auth?.user as any;
    const fallbackSubmitterEmail = state.appData?.noAccessMode
      ? String(
          state.appData?.userInfo?.email
            ?? authUser?.email
            ?? authUser?.userPrincipalName
            ?? authUser?.username
            ?? ""
        ).trim()
      : "";
    const effectiveSubmitterEmail = String(params?.submitterEmail ?? fallbackSubmitterEmail).trim();

    // Resolve category/subcategory ID → name lookup.
    // Reuse the Redux config when it was loaded for the current location/role; otherwise refetch.
    let categoryConfig = getState().ticketingPortal.submitTicketCategoryConfig;
    if (selectIsCategoryConfigStale(getState())) {
      const { params: categoryParams, contextKey } = getCategoryRequestContext(getState());
      const configRes = await api.get<{ id: number; name: string; displayOrder: number; subCategories: { id: number; name: string; displayOrder: number }[] }[]>("/ticketing/form-config/categories", { params: categoryParams });
      const fetched = configRes.data;
      // Persist in Redux so every subsequent call reads from state, not the network
      if (getCategoryRequestContext(getState()).contextKey === contextKey) {
        dispatch(setSubmitTicketFormConfigSuccess({
          categories: fetched.map((c) => c.name),
          subCategoryMap: Object.fromEntries(
            fetched.map((c) => [c.name, (c.subCategories ?? []).map((s) => s.name)])
          ),
          categoryConfig: fetched,
          contextKey,
        }));
      }
      categoryConfig = fetched;
    }

    const categoryById: Record<number, string> = {};
    const subCategoryById: Record<number, string> = {};
    categoryConfig.forEach((cat) => {
      categoryById[cat.id] = cat.name;
      (cat.subCategories ?? []).forEach((sub) => {
        subCategoryById[sub.id] = sub.name;
      });
    });

    // Build params object for axios — safer than URLSearchParams + URL concatenation
    // on iOS/Hermes where manual query-string concatenation can produce malformed URLs.
    const axiosParams: Record<string, string | number> = {
      sortBy: params?.sortBy ?? "createdAt",
      sortOrder: params?.sortOrder ?? "DESC",
      pageNumber: params?.pageNumber ?? 1,
      pageSize: params?.pageSize ?? 50,
    };
    if (params?.search) axiosParams.search = params.search;
    if (params?.status) axiosParams.status = params.status;
    if (params?.urgency) axiosParams.urgency = params.urgency;
    if (params?.categoryId != null) axiosParams.categoryId = params.categoryId;
    if (params?.subCategoryId != null) axiosParams.subCategoryId = params.subCategoryId;
    if (effectiveSubmitterEmail) axiosParams.submitterEmail = effectiveSubmitterEmail;

    const response = await api.get<{
      content: {
        id: number;
        ticketNumber: string;
        submitterEmail: string;
        title: string;
        issueDescription: string;
        categoryId: number;
        subCategoryId: number;
        ticketLevel: string;
        levelReferenceId: string;
        urgency: string;
        status: string;
        createdAt: string;
        modifiedAt: string | null;
        duplicateCount?: number;
        hasDuplicate?: boolean;
        hasDeclinedUnit?: boolean;
        absorbedTicketIds?: string[];
        mergedTicketDetails?: Array<{ ticketNumber: string; title: string }>;
        // Equipment-specific fields
        stage?: string;
        vendor?: string;
        unitCount?: number;
        noReturn?: boolean;
        conversionReference?: string;
        myTicketReason?: "SUBMITTED" | "STAKEHOLDER" | "VENDOR" | null;
      }[];
      totalElements: number;
      totalClosedTickets?: number;
    }>("/ticketing/tickets", { params: axiosParams });

    // Guard against unexpected response shapes (e.g. error objects or null on iOS)
    const content = response.data?.content ?? [];
    const closedCount = response.data?.totalClosedTickets ?? 0;
    const tickets: MyTicketRow[] = content.map((t) => {
      const isDup = (t.hasDuplicate ?? (t.duplicateCount ?? 0) > 0) && isWithin24HourDuplicateWindow(t.createdAt);
      if (isDup || t.hasDuplicate || t.duplicateCount) {
        console.log(`[fetchMyTickets] Ticket ${t.ticketNumber}: hasDuplicate=${t.hasDuplicate}, duplicateCount=${t.duplicateCount}, isFinal=${isDup}`);
      }
      return {
      id: t.ticketNumber,
      title: t.title,
      description: t.issueDescription,
      category: categoryById[t.categoryId] ?? `Category ${t.categoryId}`,
      subcategory: subCategoryById[t.subCategoryId] ?? (t.subCategoryId == null || Number(t.subCategoryId) === 0 ? "-" : `Subcategory ${t.subCategoryId}`),
      location: t.levelReferenceId,
      ticketLevel: t.ticketLevel as "Location" | "Market" | "Region",
      urgency: TICKET_URGENCY_MAP[t.urgency] ?? "",
      status: TICKET_STATUS_MAP[t.status] ?? "new",
      submitted: t.createdAt,
      lastUpdated: t.modifiedAt ?? "",
      submittedBy: t.submitterEmail,
      hasDuplicate: isDup,
      accessRequest: normalizeAccessRequestDetail((t as any).accessRequest),
      accessRequestStatus:
        (t as any).accessRequestStatus ??
        (t as any).accessRequest?.accessRequestStatus ??
        null,
      hasDeclinedUnit: t.hasDeclinedUnit === true,
      absorbedTicketIds: (t.absorbedTicketIds ?? []).map((id) => String(id || "").trim()).filter((id) => id.length > 0),
      mergedTicketsDetails: t.mergedTicketDetails ?? [],
      // Equipment-specific fields
      stage: t.stage as any,
      vendor: t.vendor ?? null,
      unitCount: t.unitCount ?? null,
      noReturn: t.noReturn ?? null,
      conversionReference: t.conversionReference ?? null,
      myTicketReason: t.myTicketReason ?? null,
      };
    });

    if (!params?.forFiltersOnly) {
      dispatch(setMyTickets(tickets));
      dispatch(setMyTicketsTotalElements(response.data?.totalElements ?? 0));
      dispatch(setClosedTicketsCount(closedCount));
    }
    return { tickets, totalElements: response.data?.totalElements ?? 0 };
  } catch (err: any) {
    if (!params?.forFiltersOnly) {
      dispatch(setMyTicketsError(err?.message || "Failed to load My Tickets"));
    }
    return { tickets: [], totalElements: 0 };
  }
};

/** Multi-select roster filters for GET /ticketing/sustain-team/members (values combined with OR within a param, AND across params). */
export interface SustainTeamMemberFilters {
  role?: string[];
  manager?: string[];
  team?: string[];
  notificationChannel?: string[];
  notificationScope?: string[];
  workingStatus?: string[];
}

export const fetchSustainTeamMembers = (
  search?: string,
  filters?: SustainTeamMemberFilters,
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setSustainTeamMembersLoading(true));
    const api = createApiClient();
    // Backend expects repeated bare keys (?role=Lead&role=Member), not the
    // bracketed/comma-joined form axios' default params serializer would produce.
    const qs = new URLSearchParams();
    if (search?.trim()) qs.set("search", search.trim());
    filters?.role?.forEach((v) => qs.append("role", v));
    filters?.manager?.forEach((v) => qs.append("manager", v));
    filters?.team?.forEach((v) => qs.append("team", v));
    filters?.notificationChannel?.forEach((v) => qs.append("notificationChannel", v));
    filters?.notificationScope?.forEach((v) => qs.append("notificationScope", v));
    filters?.workingStatus?.forEach((v) => qs.append("workingStatus", v));
    const query = qs.toString();
    const response = await api.get<{
      message: string;
      totalCount: number;
      data: SustainTeamApiMember[];
      success: boolean;
    }>(`/ticketing/sustain-team/members${query ? `?${query}` : ""}`);
    dispatch(setSustainTeamMembers({
      data: response.data?.data ?? [],
      totalCount: response.data?.totalCount ?? 0,
    }));
  } catch (err: any) {
    dispatch(setSustainTeamMembersError(err?.message || "Failed to load team members"));
  }
};

export interface UpdateSustainTeamMemberBody {
  performerEmail: string;
  performerGpid: string;
  role: string;
  /** Reporting manager's email; omit to leave unchanged, empty string to clear. */
  managerEmail?: string;
  /** Sustain Team (TKT_Teams.id) to reassign the member to; omit to leave unchanged. */
  teamId?: number;
  channelInAppOnDuty: boolean;
  channelEmailOnDuty: boolean;
  channelTeamsOnDuty: boolean;
  channelInAppOffDuty: boolean;
  channelEmailOffDuty: boolean;
  channelTeamsOffDuty: boolean;
  triggerNewTicketOnDuty: boolean;
  triggerAssignedToMeOnDuty: boolean;
  triggerUnassignedFromMeOnDuty: boolean;
  triggerCommentAddedOnDuty: boolean;
  triggerAgingTicketOnDuty: boolean;
  triggerNewTicketOffDuty: boolean;
  triggerAssignedToMeOffDuty: boolean;
  triggerUnassignedFromMeOffDuty: boolean;
  triggerCommentAddedOffDuty: boolean;
  triggerAgingTicketOffDuty: boolean;
  notificationScope: string;
  customUrgencies?: string[];
}

/**
 * PUT /ticketing/sustain-team/members/{id}
 * Updates role, notification channels/triggers/scope for a team member.
 * Refreshes the full member list on success.
 * 
 * ISSUE 1 FIX: Also reloads user profile to update frontend cache immediately.
 * This ensures the sustainTeamMember flag updates without requiring page refresh.
 */
export const updateSustainTeamMember = (
  id: string | number,
  body: UpdateSustainTeamMemberBody,
  refetch?: { search?: string; filters?: SustainTeamMemberFilters },
) => async (dispatch: AppDispatch): Promise<void> => {
  const api = createApiClient();
  await api.put(`/ticketing/sustain-team/members/${id}`, body);
  
  // Refresh list so Redux stays in sync with the server (preserving any active roster search/filters)
  dispatch(fetchSustainTeamMembers(refetch?.search ?? "", refetch?.filters));
  
  // ISSUE 1 FIX: Reload user profile to update cached sustainTeamMember flag
  // This ensures the UI immediately reflects permission changes without manual refresh
  try {
    await dispatch(reloadUserProfile());
  } catch (error) {
    logger.warn("Failed to reload user profile after updating team member", error);
    // Don't fail the operation if profile reload fails - list is already updated
  }
};

/** Raw shape of each entry returned by GET /ticketing/sustain-team/schedule */
export interface SustainTeamScheduleEntry {
  memberId: number;
  memberEmail: string;
  role: string;
  /** Reporting manager's email. Null / absent when no manager is assigned. */
  managerEmail?: string | null;
  /** TKT_Teams.id the member belongs to. Null / absent when no team is assigned. */
  teamId?: number | null;
  shiftStartCst: string;
  shiftEndCst: string;
  weekStartDate?: string;
  /** Present for members whose schedule has been explicitly configured.
   * Nested: weekStartDate (Monday) → dayName (“Mon”–“Sun”) → { is_working }.
   * The leaf has NO ‘date’ field — use the weekStartDate key + day offset to derive the date.
   */
  schedule?: Record<string, Record<string, { is_working: boolean }>>;
  updatedAt?: string;
}

/**
 * GET /ticketing/sustain-team/schedule
 * Returns shift times and current-week working-day schedule for all team members.
 * Does not write to Redux — callers receive the data directly via the returned Promise.
 */
export const fetchSustainTeamSchedule = () => async (_dispatch: AppDispatch): Promise<SustainTeamScheduleEntry[]> => {
  const api = createApiClient();
  const response = await api.get<{
    message: string;
    totalCount: number;
    data: SustainTeamScheduleEntry[];
    success: boolean;
  }>("/ticketing/sustain-team/schedule");
  return response.data?.data ?? [];
};

/**
 * PUT /ticketing/sustain-team/schedule/{memberId}
 * Saves a member's weekly working-day schedule.
 * scheduleJson is keyed by each week's Monday date (e.g. "2026-05-18"),
 * each value maps day-name ("Mon"–"Sun") to { is_working }.
 * is_working can be a boolean for standard working days or a string "HH:MM-HH:MM" for custom hours.
 */
export interface UpdateScheduleBody {
  performerEmail: string;
  performerGpid: string;
  shiftStartCst: string;
  shiftEndCst: string;
  scheduleJson: Record<string, Record<string, { is_working: boolean | string }>>;
}

export const updateSustainTeamSchedule = (
  memberId: string | number,
  body: UpdateScheduleBody,
) => async (_dispatch: AppDispatch): Promise<void> => {
  const api = createApiClient();
  await api.put(`/ticketing/sustain-team/schedule/${memberId}`, body);
};

/** Maps an API status string to the internal TicketStatus key. */
const mapApiStatus = (apiStatus: string): TicketStatus => {
  const s = (apiStatus ?? "").toLowerCase().trim();
  if (s === "new") return "new";
  if (s === "in progress") return "progress";
  if (s === "waiting on it" || s === "escalated to it") return "waiting-it";
  if (s === "waiting on tmv" || s === "escalated to tmv") return "waiting-tmv";
  if (s === "submitted to ado") return "submitted-ado";
  if (s === "waiting on user" || s === "waiting on user") return "waiting-user";
  if (s === "waiting on vendor") return "waiting-vendor";
  if (s === "closed") return "resolved";
  return "new";
};

/** Maps an API urgency string to the internal TicketUrgency key. */
const mapApiUrgency = (apiUrgency: string): TicketUrgency => {
  const u = (apiUrgency ?? "").toLowerCase().trim();
  if (u === "critical") return "critical";
  if (u === "high") return "high";
  if (u === "medium") return "medium";
  if (u === "informational") return "informational";
  return "low";
};

export const fetchSustainQueue = (filters?: {
  statuses?: string[];
  urgencies?: string[];
  categories?: string[];
  subcategories?: string[];
  locations?: string[];
  assignees?: string[];
  /** Free-text search — maps to ticketNumber / title / issueDescription / submitterEmail */
  search?: string;
  /** Comma-separated tertiary category IDs (TKT_Tertiary_Categories.id) */
  tertiaries?: string[];
  /** Comma-separated Sustain Team lead emails — matches tickets assigned to the lead
   *  directly, or to any sustain member whose manager_email is that lead. */
  managers?: string[];
  /** Comma-separated Sustain Team IDs (TKT_Teams.id) — the Team View queue scope. */
  teams?: string[];
  /** Page number for pagination (1-based). Defaults to 1. */
  pageNumber?: number;
  /** Items per page. Defaults to 100. */
  pageSize?: number;
}) => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    const pageNumber = filters?.pageNumber ?? 1;
    const pageSize = filters?.pageSize ?? 100;

    dispatch(setSustainQueueLoading(true));
    const api = createApiClient();

    // Map internal status keys → API status strings
    const STATUS_TO_API: Record<string, string> = {
      "new": "New",
      "progress": "In Progress",
      "waiting-it": "Waiting on IT",
      "waiting-tmv": "Waiting on TMV",
      "submitted-ado": "Submitted to ADO",
      "waiting-user": "Waiting on user",
      "waiting-vendor": "Waiting on Vendor",
      "resolved": "Closed",
    };

    // Map internal urgency keys → API urgency strings
    const URGENCY_TO_API: Record<string, string> = {
      "critical": "Critical",
      "high": "High",
      "medium": "Medium",
      "low": "Low",
    };

    // Build query params
    const params: Record<string, string> = {
      sortBy: "createdAt",
      sortOrder: "DESC",
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    };
    if (filters?.statuses?.length) {
      params.status = filters.statuses
        .map((s) => STATUS_TO_API[s] ?? s)
        .join(",");
    }
    if (filters?.urgencies?.length) {
      params.urgency = filters.urgencies
        .map((u) => URGENCY_TO_API[u] ?? u)
        .join(",");
    }
    if (filters?.assignees?.length) {
      // Filter assignees may be display names (e.g. "Amit Belavadi") — reverse-map to emails.
      const teamMembers = getState().ticketingPortal.sustainTeamMembers;
      const resolveEmail = (nameOrEmail: string): string => {
        if (!nameOrEmail || nameOrEmail === "Unassigned") return "null";
        if (nameOrEmail.includes("@")) return nameOrEmail;
        const match = teamMembers.find((m) => formatUserName(m.userEmail) === nameOrEmail);
        return match?.userEmail ?? nameOrEmail;
      };
      params.assignedTo = filters.assignees.map(resolveEmail).join(",");
    }
    if (filters?.categories?.length) params.category = filters.categories.join(",");
    if (filters?.subcategories?.length) params.subCategory = filters.subcategories.join(",");
    if (filters?.locations?.length) params.levelReferenceId = filters.locations.join(",");
    if (filters?.tertiaries?.length) params.tertiaryId = filters.tertiaries.join(",");
    if (filters?.managers?.length) params.manager = filters.managers.join(",");
    // Callers that don't pick a team explicitly (refreshes after merge/update, portal
    // mount, etc.) default to the logged-in user's own team.
    let teamsParam = filters?.teams;
    if (teamsParam === undefined) {
      const state = getState();
      const myEmail = (state.appData?.userInfo?.email ?? "").toLowerCase();
      const me = state.ticketingPortal.sustainTeamMembers.find(
        (m) => m.userEmail.toLowerCase() === myEmail
      );
      if (me?.teamId != null) teamsParam = [String(me.teamId)];
    }
    if (teamsParam?.length) params.assignedTeam = teamsParam.join(",");
    // Free-text search across ticketNumber, title, issueDescription, submitterEmail
    if (filters?.search?.trim()) params.search = filters.search.trim();

    const response = await api.get<{
      content: Array<{
        id: number;
        ticketNumber: string;
        submitterEmail: string;
        title: string;
        issueDescription: string;
        categoryId: number | null;
        subCategoryId: number | null;
        ticketLevel: string;
        levelReferenceId: string;
        urgency: string;
        status: string;
        affectedSystem: string | null;
        activeResolutionTimer: number | null;
        waitingOnItTimer: number | null;
        createdAt: string;
        assignedTo: string | null;
        modifiedAt: string;
        incNumber: string | null;
        duplicateCount?: number;
        hasDuplicate?: boolean;
        possibleDuplicateCount?: number;
        adoWorkItemId?: number | null;
        totalImpactedLocations?: number | null;
        assignedTeamId?: number | null;
        tertiaryId?: number | null;
        absorbedTicketIds?: string[];
        mergedTicketDetails?: Array<{ ticketNumber: string; title: string }>;
      }>;
      totalElements: number;
      totalDuplicateCount?: number;
      pageOverlapDuplicateCount?: number;
    }>("/ticketing/tickets", { params });

    const content = response.data?.content ?? [];
    const totalElements = response.data?.totalElements ?? 0;
    const apiTotalDuplicateCount = response.data?.totalDuplicateCount ?? 0;

    console.log(`[fetchSustainQueue] API totalDuplicateCount from response: ${apiTotalDuplicateCount}`);

    // Resolve category/subcategory/tertiary names from the Redux config. If the
    // cached config is empty or doesn't know every id used by these tickets
    // (stale/partial config), merge in the authoritative form-config categories.
    type NamedCategoryConfig = {
      id: number;
      name: string;
      subCategories?: {
        id: number;
        name: string;
        tertiaryCategories?: { id: number; name: string }[];
      }[];
    };
    const categoryMap: Record<number, string> = {};
    const subCategoryMap: Record<number, string> = {};
    const tertiaryMap: Record<number, string> = {};
    const addToMaps = (config: NamedCategoryConfig[]) => {
      config.forEach((cat) => {
        categoryMap[cat.id] = cat.name;
        (cat.subCategories ?? []).forEach((sub) => {
          subCategoryMap[sub.id] = sub.name;
          (sub.tertiaryCategories ?? []).forEach((ter) => {
            tertiaryMap[ter.id] = ter.name;
          });
        });
      });
    };
    const categoryContext = getCategoryRequestContext(getState());
    // Only reuse store config loaded for the current location/role; otherwise it may hold names this user shouldn't see.
    if (!selectIsCategoryConfigStale(getState())) {
      addToMaps(getState().ticketingPortal.submitTicketCategoryConfig as NamedCategoryConfig[]);
    }

    const hasUnresolvedIds = content.some(
      (t) =>
        (t.categoryId != null && categoryMap[t.categoryId] === undefined) ||
        (t.subCategoryId != null && subCategoryMap[t.subCategoryId] === undefined) ||
        (t.tertiaryId != null && tertiaryMap[t.tertiaryId] === undefined)
    );
    if (hasUnresolvedIds) {
      try {
        const configRes = await api.get<NamedCategoryConfig[]>("/ticketing/form-config/categories", { params: categoryContext.params });
        if (Array.isArray(configRes.data)) addToMaps(configRes.data);
      } catch {
        // silently fall back to IDs
      }
    }

    const now = Date.now();
    const mapped: SustainQueueRow[] = content.map((t) => {
      const activeResolutionMs = t.activeResolutionTimer ? t.activeResolutionTimer * 1000 : 0;
      // waitingOnItTimer can arrive as:
      //   1) ISO string  → backend returned timestamp directly (e.g. "2026-06-08T11:41:54Z")
      //   2) large number (> 1e9) → Unix timestamp in seconds (epoch-based point in time)
      //   3) small number          → legacy elapsed-seconds duration (deprecated)
      let waitingOnItTimerISO: string | null = null;
      if (typeof t.waitingOnItTimer === "string" && t.waitingOnItTimer) {
        // Normalize: if the backend omits the Z suffix, append it so JS parses as UTC
        // (without Z, JS treats the value as local time which adds the client's offset)
        const raw = t.waitingOnItTimer;
        waitingOnItTimerISO = /[Zz]$|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}Z`;
      } else if (typeof t.waitingOnItTimer === "number" && t.waitingOnItTimer > 1_000_000_000) {
        // Unix timestamp in seconds → convert to ISO so views can compute elapsed time
        waitingOnItTimerISO = new Date(t.waitingOnItTimer * 1000).toISOString();
      }
      // Elapsed ms for Waiting on IT: compute from the timestamp if available, else 0
      const waitingItMs = waitingOnItTimerISO
        ? Math.max(0, now - new Date(waitingOnItTimerISO).getTime())
        : 0;
      const statusEnteredAt = now - activeResolutionMs;
      const status = mapApiStatus(t.status);
      const totalMs = activeResolutionMs + waitingItMs;
      const aging = totalMs > 0
        ? (() => {
            const mins = Math.floor(totalMs / 60000);
            if (mins < 60) return `${mins}m`;
            const hours = Math.floor(mins / 60);
            if (hours < 24) return `${hours}h`;
            return `${Math.floor(hours / 24)}d`;
          })()
        : "0m";

      // Log ALL duplicate-related data for debugging
      if (t.hasDuplicate || t.duplicateCount || (t.possibleDuplicateCount ?? 0) > 0) {
        console.log(`[fetchSustainQueue] RAW API DATA for ${t.ticketNumber}:`, {
          hasDuplicate: t.hasDuplicate,
          duplicateCount: t.duplicateCount,
          possibleDuplicateCount: t.possibleDuplicateCount,
          createdAt: t.createdAt,
          within24h: isWithin24HourDuplicateWindow(t.createdAt)
        });
      }

      return {
        id: t.ticketNumber,
        title: t.title,
        description: t.issueDescription ?? "",
        category: t.categoryId ? (categoryMap[t.categoryId] ?? String(t.categoryId)) : "",
        subcategory: t.subCategoryId ? (subCategoryMap[t.subCategoryId] ?? String(t.subCategoryId)) : undefined,
        location: t.levelReferenceId === "N/A" ? "ALL" : (t.levelReferenceId ?? ""),
        ticketLevel: t.levelReferenceId === "N/A" ? undefined : ((t.ticketLevel as "Location" | "Market" | "Region") ?? "Location"),
        urgency: mapApiUrgency(t.urgency),
        status,
        submitted: t.createdAt,
        lastUpdated: t.modifiedAt,
        submittedBy: t.submitterEmail,
        assignedTo: t.assignedTo ? formatUserName(t.assignedTo) : "Unassigned",
        affectedSystem: t.affectedSystem ?? undefined,
        backendId: t.id,
        categoryNumId: t.categoryId ?? undefined,
        subCategoryNumId: t.subCategoryId ?? undefined,
        impactSites: t.totalImpactedLocations ?? 0,
        adoId: t.adoWorkItemId ? `ADO-${t.adoWorkItemId}` : undefined,
        aging,
        minutesAging: Math.round(activeResolutionMs / 60000),
        activeResolutionMs,
        waitingOnItTimerISO,
        waitingItMs,
        waitingItMinutes: Math.round(waitingItMs / 60000),
        statusEnteredAt,
        statusHistory: [{ status, enteredAt: statusEnteredAt }],
        hasDuplicate: (() => {
          const isDup = (t.hasDuplicate ?? (t.duplicateCount ?? 0) > 0) && isWithin24HourDuplicateWindow(t.createdAt);
          if (isDup || t.hasDuplicate || t.duplicateCount || (t.possibleDuplicateCount ?? 0) > 0) {
            console.log(`[fetchSustainQueue] Ticket ${t.ticketNumber}: hasDuplicate=${t.hasDuplicate}, duplicateCount=${t.duplicateCount}, possibleDuplicateCount=${t.possibleDuplicateCount}, isFinal=${isDup}`);
          }
          return isDup;
        })(),
        possibleDuplicateCount: t.possibleDuplicateCount ?? 0,
        hasDeclinedUnit: (t as { hasDeclinedUnit?: boolean }).hasDeclinedUnit === true,
        totalDuplicateCount: apiTotalDuplicateCount,
        assignedTeamId: t.assignedTeamId ?? null,
        tertiaryId: t.tertiaryId ?? null,
        tertiary: t.tertiaryId != null ? (tertiaryMap[t.tertiaryId] ?? undefined) : undefined,
        accessRequest: normalizeAccessRequestDetail((t as any).accessRequest),
        // Check top-level field first, then fall back to nested accessRequest object
        accessRequestStatus:
          (t as any).accessRequestStatus ??
          (t as any).accessRequest?.accessRequestStatus ??
          null,
        absorbedTicketIds: (t.absorbedTicketIds ?? []).map((id) => String(id || "").trim()).filter((id) => id.length > 0),
        mergedTicketsDetails: t.mergedTicketDetails ?? [],
        // Equipment-specific fields
        stage: (t as any).stage ?? null,
        vendor: (t as any).vendor ?? null,
        unitCount: (t as any).unitCount ?? null,
        noReturn: (t as any).noReturn ?? null,
        conversionReference: (t as any).conversionReference ?? null,
      };
    });

    dispatch(setSustainQueueTotalElements(totalElements));
    dispatch(setSustainQueue(mapped));
    dispatch(setSustainQueueCurrentPage(pageNumber));

    console.log(`[fetchSustainQueue] Page ${pageNumber}/${Math.ceil(totalElements / pageSize) || 1}: Loaded ${mapped.length} of ${totalElements} tickets`);
  } catch (err: any) {
    dispatch(setSustainQueueLoading(false));
    dispatch(setError(err?.message || "Failed to load Sustain Queue"));
    logger.warn("fetchSustainQueue failed:", err?.message);
  }
};

type FetchTicketDetailOptions = {
  setAsSelected?: boolean;
  silent?: boolean;
};

export const fetchTicketDetail = (
  ticketId: string,
  options: FetchTicketDetailOptions = {}
) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { setAsSelected = true, silent = false } = options;
  try {
    if (!silent) {
      dispatch(setDetailLoading(true));
    }
    if (setAsSelected) {
      dispatch(setSelectedTicketId(ticketId));
    }
    const api = createApiClient();

    // Resolve category/subcategory ID -> name lookup from Redux config.
    // Reuse the config only when it was loaded for the current location/role; otherwise refetch.
    let categoryConfig = getState().ticketingPortal.submitTicketCategoryConfig;
    if (selectIsCategoryConfigStale(getState())) {
      const { params: categoryParams, contextKey } = getCategoryRequestContext(getState());
      const configRes = await api.get<
        { id: number; name: string; displayOrder: number; subCategories: { id: number; name: string; displayOrder: number }[] }[]
      >("/ticketing/form-config/categories", { params: categoryParams });
      const fetched = configRes.data;
      if (getCategoryRequestContext(getState()).contextKey === contextKey) {
        dispatch(
          setSubmitTicketFormConfigSuccess({
            categories: fetched.map((c) => c.name),
            subCategoryMap: Object.fromEntries(
              fetched.map((c) => [c.name, (c.subCategories ?? []).map((s) => s.name)])
            ),
            categoryConfig: fetched,
            contextKey,
          })
        );
      }
      categoryConfig = fetched;
    }

    const categoryById: Record<number, string> = {};
    const subCategoryById: Record<number, string> = {};
    const tertiaryById: Record<number, string> = {};
    categoryConfig.forEach((cat) => {
      categoryById[cat.id] = cat.name;
      (cat.subCategories ?? []).forEach((sub) => {
        subCategoryById[sub.id] = sub.name;
        (sub.tertiaryCategories ?? []).forEach((tertiary) => {
          tertiaryById[tertiary.id] = tertiary.name;
        });
      });
    });

    const response = await api.get<TicketDetailApiResponse>(`/ticketing/tickets/${encodeURIComponent(ticketId)}`);
    const d = response.data;

    const categoryIdNum = Number(d.categoryId);
    const subCategoryIdNum = Number(d.subCategoryId);
    const category =
      categoryById[categoryIdNum] ??
      categoryById[Number.isNaN(categoryIdNum) ? -1 : categoryIdNum] ??
      `Category ${d.categoryId}`;
    const subcategory =
      subCategoryById[subCategoryIdNum] ??
      subCategoryById[Number.isNaN(subCategoryIdNum) ? -1 : subCategoryIdNum] ??
      `Subcategory ${d.subCategoryId}`;
    const tertiaryCategory = d.tertiaryId != null ? tertiaryById[d.tertiaryId] : undefined;

    const dynamicValues = d.dynamicValues ?? [];
    // Use dynamicValues as primary source for peopleImpacted; fallback to totalImpactedPickers.
    const peopleImpactedFromDynamic =
      dynamicValues.find((v) => /people\s*impacted/i.test(v.historicalLabel))?.value?.trim() ?? "";
    const peopleImpacted =
      peopleImpactedFromDynamic || (d.totalImpactedPickers != null ? String(d.totalImpactedPickers) : "");
    const loadsImpacted =
      dynamicValues.find((v) => /loads?\s*impacted/i.test(v.historicalLabel))?.value ?? "";

    // Get existing affected locations from Redux to preserve SAPID
    const currentState = getState();
    const existingTicket = currentState.ticketingPortal.ticketDetails[d.ticketNumber];
    const existingSapIdMap = new Map<string, string>();
    if (existingTicket?.affectedLocations) {
      existingTicket.affectedLocations.forEach((loc) => {
        if (loc.sapId) {
          existingSapIdMap.set(loc.name.toLowerCase(), loc.sapId);
        }
      });
    }

    const normalizedAffectedLocations = (d.affectedLocations ?? []).map((location, index) => {
      if (typeof location === "string") {
        return {
          id: `loc-${index}`,
          name: location,
          sapId: existingSapIdMap.get(location.toLowerCase()) || "",
          time: "",
          isSubmitter: false,
        };
      }

      // Use SAPID from API if available, otherwise try to preserve from Redux
      const apiSapId = location.sapId || "";
      const redisSapId = existingSapIdMap.get(location.locName.toLowerCase()) || "";
      const finalSapId = apiSapId || redisSapId;

      return {
        id: `${location.locId || location.locName || "loc"}-${index}`,
        name: location.locName,
        sapId: finalSapId,
        time: location.reportedTime || "",
        isSubmitter: Boolean(location.isSubmitter),
      };
    });

    const normalizedAbsorbedTicketIds = (d.absorbedTicketIds ?? [])
      .map((id) => String(id || "").trim())
      .filter((id) => id.length > 0);

    const normalizedReopenedFromId =
      typeof d.reopenedFromId === "string" && d.reopenedFromId.trim().length > 0
        ? d.reopenedFromId.trim()
        : undefined;

    const payload: TicketDetailRecord = {
      ...MOCK_TICKET_DETAIL,
      id: d.ticketNumber,
      backendTicketId: d.id,
      title: d.title,
      description: d.issueDescription,
      categoryId: Number.isNaN(categoryIdNum) ? undefined : categoryIdNum,
      subCategoryId: Number.isNaN(subCategoryIdNum) ? undefined : subCategoryIdNum,
      tertiaryId: d.tertiaryId ?? undefined,
      tertiaryCategory,
      category,
      subcategory,
      location: d.levelReferenceId,
      market: d.levelReferenceId,
      ticketLevel: (d.ticketLevel as "Location" | "Market" | "Region") || "Location",
      urgency: TICKET_URGENCY_MAP[d.urgency] ?? "",
      status: TICKET_STATUS_MAP[d.status] ?? "new",
      submitted: d.createdAt,
      lastUpdated: d.modifiedAt || d.createdAt,
      submittedBy: d.submitterEmail,
      stakeholders: (d.stakeholderEmails ?? []).join(", "),
      peopleImpacted,
      loadsImpacted,
      totalImpactedPickers: d.totalImpactedPickers,
      affectedLocations: normalizedAffectedLocations,
      accessRequest: normalizeAccessRequestDetail(d.accessRequest),
      closedAt: d.closedAt || undefined,
      reopenedFromId: normalizedReopenedFromId,
      absorbedTicketIds: normalizedAbsorbedTicketIds,
      dynamicValues,
      attachments: d.attachments ?? [],
      notes: [],
      activity: await fetchTicketActivitiesFromApi(api, d.id),
      incidentId: d.incNumber ?? undefined,
      resolutionPath: normalizeResolutionPath(d.resolutionPath),
      assignedTo: d.assignedTo ? formatUserName(d.assignedTo) : undefined,
      adoId: d.adoWorkItemId ? `ADO-${d.adoWorkItemId}` : undefined,
      isWidespread: Boolean(d.isWidespread),
      hasDuplicate: (d.hasDuplicate ?? (d.duplicateCount ?? 0) > 0) && isWithin24HourDuplicateWindow(d.createdAt),
      // Map mergedIntoTicketId: backend returns the numeric DB id of the primary ticket.
      // We store it as a string so the banner can navigate by ticket number.
      mergedIntoTicketId: d.mergedIntoTicketId != null
        ? String(d.mergedIntoTicketId)
        : undefined,
      // parentTicketRef is the human-readable ticket number of the primary ticket (e.g. "NTI-2026-00366").
      // Use this for display and navigation in the Merged-Into banner.
      parentTicketRef: d.parentTicketRef?.trim() || undefined,
      // Check top-level field first, then fall back to nested accessRequest object
      accessRequestStatus:
        (d as any).accessRequestStatus ??
        (d as any).accessRequest?.accessRequestStatus ??
        null,
      mmsToggle: d.mmsToggle ?? undefined,
      assignedTeamId: d.assignedTeamId ?? null,
      // Vendor lock / conversion split fields (present on equipment tickets)
      vendor: d.vendor ?? undefined,
      conversionReference: d.conversionReference ?? null,
      isSplitChild: d.isSplitChild ?? undefined,
      siblingTicketIds: d.siblingTicketIds ?? undefined,
      siblingTickets: d.siblingTickets ?? undefined,
      // Equipment block — present on Repair / Replace / Return tickets
      equipment: d.equipment ?? undefined,
      hasDeclinedUnit: d.hasDeclinedUnit === true,
      negligentDamageNotice: d.negligentDamageNotice === true,
    };

    dispatch(upsertTicketDetail(payload));
  } catch (err: any) {
    reportError(err);
    const statusCode = err?.response?.status;
    const errorMessage = statusCode === 404
      ? "Ticket not found. Please verify the Ticket ID."
      : (err?.message || "Failed to load ticket detail");
    if (!silent) {
      dispatch(setDetailLoading(false));
      dispatch(setError(errorMessage));
    }
  }
};

export const saveTicketDetailInfo =
  (
    ticketId: string,
    updates: EditableTicketInfo,
    dynamicValues?: Array<{ fieldConfigId: number; historicalLabel: string; value: string }>
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setDetailLoading(true));

      // Attempt a real PUT to the backend if backendTicketId is available
      const backendTicketId = resolveBackendTicketId(ticketId, getState);
      if (backendTicketId != null) {
        try {
          const state = getState();
          const detail = state.ticketingPortal.ticketDetails[ticketId];
          const catConfig = state.ticketingPortal.submitTicketCategoryConfig;
          const catEntry = catConfig.find((c) => c.name === updates.category);
          const catId = catEntry?.id ?? detail?.categoryId ?? 0;
          const subCatEntry = catEntry?.subCategories?.find((s) => s.name === updates.subcategory);
          const subCatId = subCatEntry?.id ?? detail?.subCategoryId ?? 0;
          // Only resolved when the (possibly just-changed) sub-category has tertiary options and one is selected.
          const tertiaryId = updates.tertiaryCategory
            ? subCatEntry?.tertiaryCategories?.find((t) => t.name === updates.tertiaryCategory)?.id
            : undefined;
          const api = createApiClient();
          const clientTimeParams = getClientTimeQueryParams();
          const stakeholderEmails = (() => {
            const raw = updates.stakeholders;
            if (!raw) return [];
            try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; }
            catch { return raw.split(",").map((s) => s.trim()).filter(Boolean); }
          })();

          // Build locations — mirrors SubmitTicketScreen.buildSubmitLocations()
          const appLocations: any[] = (state as any).appData?.locations ?? [];
          const selectedLevel     = (updates.ticketLevel ?? "").trim().toLowerCase();
          const selectedReference = (updates.market ?? "").trim().toLowerCase();
          const locations = (() => {
            if (!selectedLevel || !selectedReference) return [];
            const filtered = appLocations.filter((loc: any) => {
              const locName = (loc?.name ?? "").trim().toLowerCase();
              const locId   = String(loc?.locationId ?? "").trim().toLowerCase();
              if (selectedLevel === "location") return locName === selectedReference || locId === selectedReference;
              if (selectedLevel === "region")   return (loc?.region ?? "").trim().toLowerCase() === selectedReference;
              if (selectedLevel === "market")   return (loc?.market ?? "").trim().toLowerCase() === selectedReference;
              return false;
            });
            const deduped: Record<string, any> = {};
            filtered.forEach((loc: any, idx: number) => {
              const locName = (loc?.name ?? "").trim();
              const locId   = String(loc?.locationId ?? "").trim();
              if (!locName || !locId) return;
              const key = locName.toLowerCase();
              if (!deduped[key]) {
                deduped[key] = {
                  locName,
                  locId,
                  sapId:      (loc?.locSapId ?? "").trim() || undefined,
                  region:     (loc?.region   ?? "").trim() || undefined,
                  market:     (loc?.market   ?? "").trim() || undefined,
                  isSubmitter: selectedLevel === "location" ? idx === 0 : false,
                };
              }
            });
            return Object.values(deduped);
          })();

          await api.put(
            `/ticketing/tickets/${backendTicketId}`,
            {
              submitterEmail: detail?.submittedBy ?? "",
              title: detail?.title ?? "",
              issueDescription: updates.description,
              categoryId: catId,
              subCategoryId: subCatId,
              tertiaryId: tertiaryId ?? null,
              ticketLevel: updates.ticketLevel,
              levelReferenceId: updates.market,
              urgency: updates.urgency,
              status: detail?.status ?? "new",
              assignedTo: detail?.assignedTo ?? "",
              resolutionPath: detail?.resolutionPath ?? "",
              stakeholderEmails,
              peopleImpacted: updates.peopleImpacted,
              loadsImpacted: updates.loadsImpacted,
              locations,
              ...(dynamicValues !== undefined ? { dynamicValues } : {}),
            },
            { params: clientTimeParams }
          );
          // Enrich updates with resolved IDs for the Redux reducer
          const enriched: any = { ...updates, categoryId: catId, subCategoryId: subCatId, tertiaryId: tertiaryId ?? undefined };
          if (dynamicValues !== undefined) enriched.dynamicValues = dynamicValues;
          dispatch(updateTicketDetailInfo({ ticketId, updates: enriched }));
          dispatch(setDetailLoading(false));
          return;
        } catch (apiErr: any) {
          // API not yet available – fall through to local-only update
          logger.warn("saveTicketDetailInfo: PUT failed, falling back to local update", apiErr?.message);
        }
      }

      // Local-only fallback (no backend available)
      await new Promise((resolve) => setTimeout(resolve, 250));
      const localPayload: any = dynamicValues !== undefined ? { ...updates, dynamicValues } : updates;
      dispatch(updateTicketDetailInfo({ ticketId, updates: localPayload }));
      dispatch(setDetailLoading(false));
    } catch (err: any) {
      dispatch(setError(err?.message || "Failed to save ticket details"));
      dispatch(setDetailLoading(false));
    }
  };

export const fetchTicketActivities = (ticketId: string) => async (dispatch: AppDispatch, getState: () => RootState): Promise<ActivityItem[]> => {
  try {
    const api = createApiClient();
    const backendTicketId = resolveBackendTicketId(ticketId, getState);
    if (backendTicketId == null) {
      return [];
    }

    const activities = await fetchTicketActivitiesFromApi(api, backendTicketId);
    const existing = getState().ticketingPortal.ticketDetails[ticketId];
    if (existing) {
      dispatch(upsertTicketDetail({ ...existing, activity: activities }));
    }
    return activities;
  } catch {
    return [];
  }
};

export const postTicketComment = (ticketId: string, commentText: string, fileNames: string[] = [], actorRoleTag: string = "Sustain Team") => async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
  const content = commentText.trim();
  // Allow image-only submissions; reject only when both text and images are empty
  if (!content && fileNames.length === 0) {
    return false;
  }
  try {
    const api = createApiClient();
    const backendTicketId = resolveBackendTicketId(ticketId, getState);
    const actorEmail = String((getState() as any).appData?.userInfo?.email || "current.user@pepsico.com");
    const actorName = formatUserName(actorEmail) || "You";

    if (backendTicketId != null) {
      const clientTimeParams = getClientTimeQueryParams();
      
      // fileNames are already uploaded by the component; build activityImageResponse object
      const uploadedFileNames = fileNames;
      const activityImageResponse = uploadedFileNames.length > 0
        ? uploadedFileNames.reduce<Record<string, string>>((acc, name, idx) => {
            acc[String(idx + 1)] = name;
            return acc;
          }, {})
        : undefined;
      
      const response = await api.post<TicketActivityApiResponse>("/ticketing/activities", {
        ticketId: backendTicketId,
        actorName,
        actorRoleTag,
        activityType: "Comment",
        activityText: content,
        isServiceNow: false,
        createdByUserId: actorEmail,
        ...(activityImageResponse !== undefined ? { activityImageResponse } : {}),
      }, {
        params: {
          clientTimezone: clientTimeParams.clientTimezone,
        },
      });

      const createdActivity = mapApiActivityToItem(response.data);
      dispatch(addTicketActivityComment({
        ticketId,
        activity: uploadedFileNames.length > 0 ? { ...createdActivity, images: uploadedFileNames } : createdActivity,
      }));
      
      // Refetch the ticket detail to get updated status (e.g. "Waiting on User") from backend
      // This ensures the UI reflects any status changes triggered by the comment
      dispatch(fetchTicketDetail(ticketId) as any).catch((err: any) => {
        console.warn("[postTicketComment] Failed to refresh ticket detail:", err?.message);
      });
      
      // Also refetch notifications so new notifications appear immediately (e.g. status change notifications)
      dispatch(fetchActiveNotifications()).catch((err: any) => {
        console.warn("[postTicketComment] Failed to refresh notifications:", err?.message);
      });
      
      // Also refetch DLO notifications so action plan notifications appear in header bell immediately
      dispatch(refetchNotifications() as any).catch((err: any) => {
        console.warn("[postTicketComment] Failed to refresh DLO notifications:", err?.message);
      });
      
      return true;
    }

    // Fallback to local state when backend ticket id is unavailable.
    const activity: ActivityItem = {
      id: `a${Date.now()}`,
      authorInitials: "ME",
      authorName: "You",
      role: "Sustain Team",
      time: "Just now",
      content,
      type: "sustain",
      ...(fileNames.length > 0 ? { images: fileNames } : {}),
    };
    dispatch(addTicketActivityComment({ ticketId, activity }));
    return true;
  } catch {
    // Compatibility fallback: preserve pre-integration behavior if API post fails.
    const activity: ActivityItem = {
      id: `a${Date.now()}`,
      authorInitials: "ME",
      authorName: "You",
      role: "Sustain Team",
      time: "Just now",
      content,
      type: "sustain",
      ...(fileNames.length > 0 ? { images: fileNames } : {}),
    };
    dispatch(addTicketActivityComment({ ticketId, activity }));
    return true;
  }
};

const resolveBackendTicketId = (ticketId: string, getState: () => RootState): number | null => {
  const numericId = Number.parseInt(ticketId, 10);
  if (!Number.isNaN(numericId)) {
    return numericId;
  }

  const state = getState().ticketingPortal;
  const detail = state.ticketDetails[ticketId];
  if (detail?.backendTicketId != null) {
    return detail.backendTicketId;
  }

  const queueRow = state.sustainQueue.find((row) => row.id === ticketId);
  if (queueRow?.backendId != null) {
    return queueRow.backendId;
  }

  return null;
};

export const saveTicketInternalNote = (ticketId: string, noteText: string, fileNames: string[] = []) => async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
  const body = noteText.trim();
  if (!body && fileNames.length === 0) {
    return false;
  }
  try {
    const api = createApiClient();

    const ticketIdNum = resolveBackendTicketId(ticketId, getState);
    if (ticketIdNum == null) {
      console.warn("Invalid ticket ID for API call:", ticketId);
      return false;
    }

    const actorEmail = String((getState() as any).appData?.userInfo?.email || "current.user@pepsico.com");
    // fileNames are already uploaded by the component; build imageResponse object directly
    const imageResponse = fileNames.length > 0
      ? fileNames.reduce<Record<string, string>>((acc, name, idx) => {
          acc[String(idx + 1)] = name;
          return acc;
        }, {})
      : undefined;
    const clientTimeParams = getClientTimeQueryParams();

    const response = await api.post<any>(`/ticketing/tickets/${ticketIdNum}/notes`, {
      commentText: body,
      createdBy: actorEmail,
      ...(imageResponse !== undefined ? { imageResponse } : {}),
    }, {
      params: {
        clientTimezone: clientTimeParams.clientTimezone,
      },
    });

    if (response.status === 200 && response.data) {
      await dispatch(fetchTicketNotes(ticketId) as any);
      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to save note:", error);
    return false;
  }
};

export const fetchTicketNotes = (ticketId: string) => async (dispatch: AppDispatch, getState: () => RootState): Promise<NoteItem[]> => {
  try {
    const api = createApiClient();

    const ticketIdNum = resolveBackendTicketId(ticketId, getState);
    if (ticketIdNum == null) {
      console.warn("Invalid ticket ID for API call:", ticketId);
      return [];
    }

    const response = await api.get<any[]>(`/ticketing/tickets/${ticketIdNum}/notes`);
    
    if (response.status === 200 && Array.isArray(response.data)) {
      const notes: NoteItem[] = response.data.map(mapApiNoteToItem);
      dispatch(setTicketNotes({ ticketId, notes }));
      return notes;
    }
    dispatch(setTicketNotes({ ticketId, notes: [] }));
    return [];
  } catch (error) {
    console.error("Failed to fetch notes:", error);
    dispatch(setTicketNotes({ ticketId, notes: [] }));
    return [];
  }
};

/** Maps internal status key → API status string for PUT /ticketing/tickets/{id} */
const SUSTAIN_STATUS_TO_API: Record<string, string> = {
  new: "New",
  progress: "In Progress",
  "waiting-it": "Waiting on IT",
  "waiting-tmv": "Waiting on TMV",
  "submitted-ado": "Submitted to ADO",
  "waiting-user": "Waiting on User",
  "waiting-vendor": "Waiting on Vendor",
  resolved: "Closed",
};

/**
 * Normalize incoming status values (which may be either internal keys like
 * 'waiting-it' or human-facing labels like 'Escalated to IT') into the
 * canonical internal status keys used throughout the app.
 */
const normalizeIncomingStatus = (s?: string | null): string | undefined => {
  if (!s) return s as any;
  const v = String(s).trim().toLowerCase();
  if (v === "escalated to it" || v === "waiting on it") return "waiting-it";
  if (v === "escalated to tmv" || v === "waiting on tmv") return "waiting-tmv";
  if (v === "submitted to ado") return "submitted-ado";
  if (v === "waiting on user") return "waiting-user";
  if (v === "waiting on vendor") return "waiting-vendor";
  if (v === "in progress") return "progress";
  if (v === "new") return "new";
  if (v === "closed" || v === "resolved") return "resolved";
  return s;
};

/** Maps internal urgency key → API urgency string for PUT /ticketing/tickets/{id} */
const SUSTAIN_URGENCY_TO_API: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  informational: "Informational",
};

/**
 * PUT /api/ticketing/tickets/{backendId} then re-fetch the full sustain queue.
 * Used for quick-edit (status, urgency, assignedTo) from Kanban and Table views.
 */
export const updateSustainTicket = (
  ticketId: string,
  updates: Partial<Pick<SustainQueueRow, "status" | "urgency" | "assignedTo">>,
  refreshFilters?: {
    statuses?: string[];
    urgencies?: string[];
    categories?: string[];
    subcategories?: string[];
    locations?: string[];
    assignees?: string[];
    search?: string;
    pageNumber?: number;
    pageSize?: number;
  },
  closeMetadata?: {
    categoryId: number;
    subCategoryId: number;
    rcaCategory: string;
    resolvedBy: string;
  },
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  const queue = getState().ticketingPortal.sustainQueue;
  const ticket = queue.find((t) => t.id === ticketId);

  if (!ticket || ticket.backendId == null) {
    logger.warn("updateSustainTicket: ticket not found or missing backendId", ticketId);
    return;
  }

  const members = getState().ticketingPortal.sustainTeamMembers;

  /**
   * The dropdown stores display names (e.g. "Amit Belavadi") formatted from
   * emails via formatUserName. Reverse-map back to email for the API.
   * If the value already looks like an email or is "Unassigned", pass through.
   */
  const resolveAssigneeEmail = (nameOrEmail: string): string => {
    if (!nameOrEmail || nameOrEmail === "Unassigned") return "";
    if (nameOrEmail.includes("@")) return nameOrEmail;
    const match = members.find((m) => formatUserName(m.userEmail) === nameOrEmail);
    return match?.userEmail ?? nameOrEmail;
  };

  let mergedStatus = normalizeIncomingStatus(updates.status ?? ticket.status) as any;
  const mergedUrgency = updates.urgency ?? ticket.urgency;
  const rawAssignedTo = updates.assignedTo ?? ticket.assignedTo;
  const mergedAssignedTo = resolveAssigneeEmail(rawAssignedTo);
  const clientTimeParams = getClientTimeQueryParams();

  const body = {
    submitterEmail: ticket.submittedBy ?? "",
    title: ticket.title,
    issueDescription: ticket.description,
    categoryId: closeMetadata?.categoryId ?? ticket.categoryNumId ?? 0,
    subCategoryId: closeMetadata?.subCategoryId ?? ticket.subCategoryNumId ?? 0,
    ...(closeMetadata?.rcaCategory ? { rcaCategory: closeMetadata.rcaCategory } : {}),
    ...(closeMetadata?.resolvedBy ? { resolvedBy: closeMetadata.resolvedBy } : {}),
    ticketLevel: ticket.ticketLevel ?? "Location",
    levelReferenceId: ticket.location ?? "",
    urgency: mergedUrgency ? (SUSTAIN_URGENCY_TO_API[mergedUrgency] ?? mergedUrgency) : null,
    status: SUSTAIN_STATUS_TO_API[mergedStatus] ?? mergedStatus,
    assignedTo: mergedAssignedTo,
    // Send ISO timestamp string when entering waiting-it so the backend can
    // start/resume the Waiting-on-IT timer. Null for all other statuses.
    waitingOnItTimer: mergedStatus === "waiting-it" ? clientTimeParams.clientTimestamp : null,
  };

  try {
    const api = createApiClient();
    await api.put(`/ticketing/tickets/${ticket.backendId}`, body, {
      params: clientTimeParams,
    });
  } catch (err: any) {
    logger.warn("updateSustainTicket PUT failed:", err?.message);
  }

  // Re-fetch the queue to sync the latest data (regardless of PUT success)
  dispatch(fetchSustainQueue(refreshFilters));
};

/**
 * PUT /ticketing/tickets/{backendId} with triage field updates (resolution path, assigned to, incident #)
 * Updates the ticket with new resolution path, assigned team member, and incident number.
 * If status upgrade needed (New → In Progress), includes it.
 */
export interface UpdateTriageFieldsParams {
  resolutionPath: string;
  assignedTo: string;
  incidentNumber: string;
  status?: TicketStatus;
  adoWorkItemId?: number;
  categoryId?: number;
  subCategoryId?: number;
  rcaCategory?: string;
  resolvedBy?: string;
}

export const updateTriageFields = (
  ticketId: string,
  updates: UpdateTriageFieldsParams,
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  try {
    const state = getState();
    const detail = state.ticketingPortal.ticketDetails[ticketId];
    
    if (!detail || !detail.backendTicketId) {
      logger.warn("updateTriageFields: ticket not found or missing backendTicketId", ticketId);
      throw new Error("Ticket not found");
    }

    const members = state.ticketingPortal.sustainTeamMembers;

    // Resolve assignee name to email
    const resolveAssigneeEmail = (nameOrEmail: string): string => {
      if (!nameOrEmail || nameOrEmail === "Unassigned") return "";
      if (nameOrEmail.includes("@")) return nameOrEmail;
      const match = members.find((m) => formatUserName(m.userEmail) === nameOrEmail);
      return match?.userEmail ?? nameOrEmail;
    };

    const mergedAssignedTo = resolveAssigneeEmail(updates.assignedTo);
    let mergedStatus = normalizeIncomingStatus(updates.status ?? detail.status) as any;
    const clientTimeParams = getClientTimeQueryParams();

    const body = {
      submitterEmail: detail.submittedBy ?? "",
      title: detail.title,
      issueDescription: detail.description,
      categoryId: updates.categoryId ?? detail.categoryId ?? 0,
      subCategoryId: updates.subCategoryId ?? detail.subCategoryId ?? 0,
      ...(updates.rcaCategory !== undefined ? { rcaCategory: updates.rcaCategory } : {}),
      ...(updates.resolvedBy !== undefined ? { resolvedBy: updates.resolvedBy } : {}),
      ticketLevel: detail.ticketLevel ?? "Location",
      levelReferenceId: detail.location ?? detail.market ?? "",
      urgency: detail.urgency ? (SUSTAIN_URGENCY_TO_API[detail.urgency] ?? detail.urgency) : null,
      status: SUSTAIN_STATUS_TO_API[mergedStatus] ?? mergedStatus,
      assignedTo: mergedAssignedTo,
      resolutionPath: updates.resolutionPath || undefined,
      incNumber: updates.incidentNumber || undefined,
      // Send ISO timestamp string when entering waiting-it so the backend can
      // start/resume the Waiting-on-IT timer. Null for all other statuses.
      waitingOnItTimer: mergedStatus === "waiting-it" ? clientTimeParams.clientTimestamp : null,
      ...(updates.adoWorkItemId !== undefined ? { adoWorkItemId: updates.adoWorkItemId } : {}),
    };

    const api = createApiClient();
    await api.put(`/ticketing/tickets/${detail.backendTicketId}`, body, {
      params: clientTimeParams,
    });

    // Update local state
    const updatedDetail: TicketDetailRecord = {
      ...detail,
      resolutionPath: updates.resolutionPath,
      assignedTo: updates.assignedTo,
      incidentId: updates.incidentNumber,
      status: mergedStatus,
      categoryId: updates.categoryId ?? detail.categoryId,
      subCategoryId: updates.subCategoryId ?? detail.subCategoryId,
      ...(updates.adoWorkItemId !== undefined ? { adoId: `ADO-${updates.adoWorkItemId}` } : {}),
    };

    dispatch(upsertTicketDetail(updatedDetail));

    // Re-fetch detail to keep right-side Activity panel aligned with backend.
    // IMPORTANT: Add a small delay before fetchTicketDetail to ensure the immediately-dispatched
    // fetchTicketActivities (from confirmCreateAdoWorkItem) completes its Redux update before
    // fetchTicketDetail runs its inline fetchTicketActivitiesFromApi, which would otherwise
    // overwrite the freshly-added activity with stale data.
    setTimeout(() => {
      dispatch(fetchTicketDetail(ticketId) as any);
    }, 200);
    
    dispatch(fetchSustainQueue());
  } catch (err: any) {
    logger.warn("updateTriageFields failed:", err?.message);
    throw err;
  }
};

/**
 * PUT /ticketing/tickets/{backendId} with a new assignedTeamId (manual team reassignment).
 * Per product spec, reassigning the team also unassigns the ticket from any user
 * (assignedTo cleared) — the ticket falls back into the new team's "New" queue.
 */
export const updateAssignedTeam = (
  ticketId: string,
  teamId: number,
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  const state = getState();
  const detail = state.ticketingPortal.ticketDetails[ticketId];

  if (!detail || !detail.backendTicketId) {
    logger.warn("updateAssignedTeam: ticket not found or missing backendTicketId", ticketId);
    throw new Error("Ticket not found");
  }

  const clientTimeParams = getClientTimeQueryParams();

  const body = {
    submitterEmail: detail.submittedBy ?? "",
    title: detail.title,
    issueDescription: detail.description,
    categoryId: detail.categoryId ?? 0,
    subCategoryId: detail.subCategoryId ?? 0,
    ticketLevel: detail.ticketLevel ?? "Location",
    levelReferenceId: detail.location ?? detail.market ?? "",
    urgency: SUSTAIN_URGENCY_TO_API[detail.urgency] ?? detail.urgency,
    status: SUSTAIN_STATUS_TO_API[detail.status] ?? detail.status,
    assignedTo: "",
    resolutionPath: detail.resolutionPath || undefined,
    incNumber: detail.incidentId || undefined,
    waitingOnItTimer: detail.status === "waiting-it" ? clientTimeParams.clientTimestamp : null,
    assignedTeamId: teamId,
  };

  try {
    const api = createApiClient();
    await api.put(`/ticketing/tickets/${detail.backendTicketId}`, body, {
      params: clientTimeParams,
    });

    dispatch(upsertTicketDetail({
      ...detail,
      assignedTeamId: teamId,
      assignedTo: undefined,
    }));

    dispatch(fetchTicketActivities(ticketId) as any);
    dispatch(fetchSustainQueue());
  } catch (err: any) {
    logger.warn("updateAssignedTeam failed:", err?.message);
    throw err;
  }
};

// ─── Add Affected Location ──────────────────────────────────────────────────

export type AddAffectedLocationResult = {
  alreadyExists: boolean;
  totalAffectedLocations: number;
  message: string;
};

export type GetAffectedLocationsResult = {
  ticketNumber: string;
  ticketExists: boolean;
  totalCount: number;
  totalAffectedUsers?: number | null;
  locations: Array<{
    id: number;
    locName: string;
    locId: string;
    sapId?: string | null;
    createdBy?: string | null;
    reportedTime?: string | null;
    isSubmitter: boolean;
    createdAt?: string | null;
  }>;
  alreadyAttachedForLocation?: boolean | null;
};

/**
 * POST /ticketing/tickets/{ticketNumber}/affected-locations
 *
 * Used in two places:
 *  1. Landing page "I'm Affected Too" banner — passes the user's loc from their profile.
 *  2. Ticket Detail "Add Location" — Sustain member adds a location manually.
 *
 * On success the Redux ticket detail is refreshed so the UI reflects the new location.
 */
export const addAffectedLocationToTicket = (
  ticketNumber: string,
  locName: string,
  locId: string,
  sapId: string | null,
  requesterEmail?: string,
) => async (dispatch: AppDispatch): Promise<AddAffectedLocationResult> => {
  const api = createApiClient();
  const body: Record<string, unknown> = { locName, locId };
  if (sapId) body.sapId = sapId;

  const query = requesterEmail?.trim()
    ? `requesterEmail=${encodeURIComponent(requesterEmail.trim())}`
    : "";
  const response = await api.post<AddAffectedLocationResult>(
    query
      ? `/ticketing/tickets/${encodeURIComponent(ticketNumber)}/affected-locations?${query}`
      : `/ticketing/tickets/${encodeURIComponent(ticketNumber)}/affected-locations`,
    body,
  );
  const data = response.data;

  // Refresh ticket detail so the locations list updates in the UI
  dispatch(fetchTicketDetail(ticketNumber) as any);

  return data;
};

/**
 * GET /ticketing/tickets/{ticketNumber}/affected-locations
 * Optional locName param returns alreadyAttachedForLocation so UI can disable
 * the "I'm Affected Too" button before user interaction.
 */
export const getAffectedLocationsForTicket = (
  ticketNumber: string,
  locName?: string,
) => async (_dispatch: AppDispatch): Promise<GetAffectedLocationsResult> => {
  const api = createApiClient();
  const query = locName?.trim() ? `locName=${encodeURIComponent(locName.trim())}` : "";
  const url = query
    ? `/ticketing/tickets/${encodeURIComponent(ticketNumber)}/affected-locations?${query}`
    : `/ticketing/tickets/${encodeURIComponent(ticketNumber)}/affected-locations`;

  const response = await api.get<GetAffectedLocationsResult>(url);
  return response.data;
};

export type RemoveAffectedLocationResult = {
  ticketNumber: string;
  locName: string;
  removed: boolean;
  message: string;
  totalAffectedLocations: number;
};

export const removeAffectedLocationFromTicket = (
  ticketNumber: string,
  locName: string,
) => async (dispatch: AppDispatch): Promise<RemoveAffectedLocationResult> => {
  const api = createApiClient();
  const response = await api.delete<RemoveAffectedLocationResult>(
    `/ticketing/tickets/${encodeURIComponent(ticketNumber)}/affected-locations?locName=${encodeURIComponent(locName)}`
  );

  // Refresh ticket detail so locations list stays in sync with backend.
  dispatch(fetchTicketDetail(ticketNumber) as any);

  return response.data;
};

export type StakeholderMutationResult = {
  alreadyAttached?: boolean;
  message?: string;
  affectedLocationsCount?: number;
};

/**
 * POST /ticketing/tickets/{backendTicketId}/stakeholders
 * Adds a secondary stakeholder to an existing ticket from the Ticket Detail "Secondary Stakeholders" card.
 */
export const addTicketDetailStakeholder = (
  ticketNumber: string,
  backendTicketId: number,
  stakeholderEmail: string,
) => async (dispatch: AppDispatch): Promise<StakeholderMutationResult> => {
  const api = createApiClient();
  const response = await api.post<StakeholderMutationResult>(
    `/ticketing/tickets/${backendTicketId}/stakeholders`,
    { stakeholderEmail },
  );

  // Refresh ticket detail so the stakeholders list stays in sync with backend.
  dispatch(fetchTicketDetail(ticketNumber) as any);

  return response.data ?? {};
};

/**
 * Fetches the full list of locations from the same admin locations API used by
 * Location Management and stores the normalized subset needed by Ticketing.
 */
export const fetchAllLocations = () => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  // Skip if we already have data to avoid redundant network calls on every
  // screen mount.
  if (getState().ticketingPortal.allLocations.length > 0) return;
  try {
    const pageSize = 200;
    const allItems: Array<{ id: string; sap: string; name: string; region: string; market: string; status: string }> = [];
    let page = 0;
    let expectedTotalPages: number | null = null;
    let fetchedPages = 0;
    const maxPageGuard = 500;

    while (page < maxPageGuard) {
      const pageResult = await locationApi.getAll(DEFAULT_FILTERS, page, pageSize);
      fetchedPages += 1;
      allItems.push(...pageResult.items);

      if (expectedTotalPages == null && Number.isFinite(pageResult.totalPages) && pageResult.totalPages > 0) {
        expectedTotalPages = pageResult.totalPages;
      }

      const reachedReportedLastPage = expectedTotalPages != null && page + 1 >= expectedTotalPages;
      const receivedShortPage = pageResult.items.length < pageSize;
      const noMoreItems = pageResult.items.length === 0;

      if (reachedReportedLastPage || receivedShortPage || noMoreItems) {
        break;
      }

      page += 1;
    }

    if (page >= maxPageGuard) {
      logger.warn("[fetchAllLocations] Reached pagination safety cap", { maxPageGuard, fetchedPages });
    }

    const normalized: LocationItem[] = allItems.map((location) => ({
      id: Number(location.id) || 0,
      locId: String(location.id ?? "").trim(),
      locSapId: String(location.sap ?? "").trim(),
      locName: String(location.name ?? "").trim(),
      region: String(location.region ?? "").trim(),
      market: String(location.market ?? "").trim(),
      status: String(location.status ?? "").trim(),
    }));

    const active = normalized.filter((location) => location.status === "Active" || !location.status);
    dispatch(setAllLocations(active));
    logger.log(
      `[fetchAllLocations] Loaded ${active.length} active locations from ${allItems.length} total rows across ${fetchedPages} pages` +
      `${expectedTotalPages != null ? ` (backend reported totalPages=${expectedTotalPages})` : ""}`
    );
  } catch (err: any) {
    logger.warn("[fetchAllLocations] Failed:", err?.message);
    // Non-fatal — the filter dropdowns will fall back to values derived from
    // the sustain queue data.
  }
};

// ── Access Request Triage Actions ─────────────────────────────────────────────

export type ARTriageAction = "approve" | "modify" | "deny";

export interface ARTriageModifiedFields {
  accessType?: string;
  organization?: string;
  department?: string;
  role?: string;
  roleId?: number;
  locations?: Array<{
    locationType: string;
    locationId: string;
    locationName: string;
    region?: string | null;
    market?: string | null;
    site?: string | null;
  }>;
}

export interface ARTriagePayload {
  action: ARTriageAction;
  modificationNotes?: string;
  modifiedFields?: ARTriageModifiedFields;
  denialReason?: string;
}

/**
 * POST /api/ticketing/tickets/access-requests/{id}/triage
 * Handles approve, modify, and deny actions for Access Request tickets.
 * Returns { success: boolean; message: string } so callers can show feedback.
 */
export const triageAccessRequest = (
  backendTicketId: number,
  payload: ARTriagePayload,
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<{ success: boolean; message: string }> => {
  try {
    const api = createApiClient();
    const { clientTimestamp, clientTimezone } = getClientTimeQueryParams();
    await api.post(
      `/ticketing/tickets/access-requests/${backendTicketId}/triage`,
      payload,
      { params: { clientTimestamp, clientTimezone } },
    );
    // Re-fetch ticket detail to sync latest status/state
    const state = getState();
    const ticketNumber = state.ticketingPortal.ticketDetails
      ? Object.values(state.ticketingPortal.ticketDetails).find(
          (t: any) => t?.backendTicketId === backendTicketId
        )?.id ?? ""
      : "";
    if (ticketNumber) {
      dispatch(fetchTicketDetail(ticketNumber) as any);
    }
    return { success: true, message: "Action completed successfully." };
  } catch (err: any) {
    const msg = err?.response?.data?.message ?? err?.message ?? "Triage action failed.";
    logger.warn("[triageAccessRequest] Failed:", msg);
    return { success: false, message: msg };
  }
};

/**
 * POST /api/ticketing/tickets/access-requests/{id}/resolve-by-it
 * Closes an Access Request ticket that was escalated to Waiting on IT.
 */
export const resolveAccessRequestByIT = (
  backendTicketId: number,
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<{ success: boolean; message: string }> => {
  try {
    const api = createApiClient();
    const { clientTimestamp, clientTimezone } = getClientTimeQueryParams();
    await api.post(
      `/ticketing/tickets/access-requests/${backendTicketId}/resolve-by-it`,
      {},
      { params: { clientTimestamp, clientTimezone } },
    );
    // Re-fetch ticket detail to sync latest status
    const state = getState();
    const ticketNumber = state.ticketingPortal.ticketDetails
      ? Object.values(state.ticketingPortal.ticketDetails).find(
          (t: any) => t?.backendTicketId === backendTicketId
        )?.id ?? ""
      : "";
    if (ticketNumber) {
      dispatch(fetchTicketDetail(ticketNumber) as any);
    }
    return { success: true, message: "Ticket resolved by IT." };
  } catch (err: any) {
    const msg = err?.response?.data?.message ?? err?.message ?? "Resolve-by-IT failed.";
    logger.warn("[resolveAccessRequestByIT] Failed:", msg);
    return { success: false, message: msg };
  }
};

/**
 * Helper: Convert ISO datetime to "YYYY-MM-DDTHH:mm" format for HomepageBannerNotif.endDate
 */
function toDatetimeLocal(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Map backend notification type strings to frontend NotificationType enum
 * Backend returns full type names like "Scheduled Maintenance", "Active Incident", etc.
 */
const NOTIF_TYPE_MAP: Record<string, "incident" | "maint" | "general"> = {
  "Active Incident": "incident",
  "Widespread Incident": "incident",
  "Scheduled Maintenance": "maint",
  "General Announcement": "general",
};

/**
 * Fetch all active incident and homepage notifications from the API.
 * Called on TicketingPortal landing screen load to display banners automatically.
 * 
 * GET /ticketing/notifications?size=100&sortBy=createdAt&sortDir=desc
 * Returns notifications where triggerBannerNotification=true to display on homepage.
 */
// ─── All Members Off Duty ─────────────────────────────────────────────────────

/** Shape returned by all three All-Members-Off-Duty endpoints */
export interface SustainAllMembersOffDutyResponse {
  enabled: boolean;
  activeNow: boolean;
  startDate: string | null;     // yyyy-MM-dd
  endDate: string | null;       // yyyy-MM-dd
  startTime: string | null;     // HH:mm
  endTime: string | null;       // HH:mm
  startDateTime: string | null; // yyyy-MM-ddTHH:mm:ss
  endDateTime: string | null;   // yyyy-MM-ddTHH:mm:ss
  updatedBy: string | null;
  updatedAt: string | null;     // yyyy-MM-ddTHH:mm:ss
}

export interface SustainAllMembersOffDutyActivateRequest {
  performerEmail: string;
  performerGpid?: string;
  startDate: string; // yyyy-MM-dd
  endDate: string;   // yyyy-MM-dd
  startTime: string; // HH:mm
  endTime: string;   // HH:mm
}

export interface SustainAllMembersOffDutyDeactivateRequest {
  performerEmail: string;
  performerGpid?: string;
}

/**
 * GET /ticketing/sustain-team/all-members-off-duty/status
 * Returns the current all-members-off-duty toggle state.
 */
export const fetchAllMembersOffDutyStatus =
  () => async (_dispatch: AppDispatch): Promise<SustainAllMembersOffDutyResponse> => {
    const api = createApiClient();
    const resp = await api.get<{ success: boolean; message: string; data: SustainAllMembersOffDutyResponse }>(
      "/ticketing/sustain-team/all-members-off-duty/status",
    );
    return resp.data.data;
  };

/**
 * POST /ticketing/sustain-team/all-members-off-duty/activate
 * Activates the all-members-off-duty window for the specified date/time range.
 */
export const activateAllMembersOffDuty =
  (body: SustainAllMembersOffDutyActivateRequest) =>
  async (_dispatch: AppDispatch): Promise<SustainAllMembersOffDutyResponse> => {
    const api = createApiClient();
    const resp = await api.post<{ success: boolean; message: string; data: SustainAllMembersOffDutyResponse }>(
      "/ticketing/sustain-team/all-members-off-duty/activate",
      body,
    );
    if (!resp.data.success) {
      throw new Error(resp.data.message || "Activation failed");
    }
    return resp.data.data;
  };

/**
 * POST /ticketing/sustain-team/all-members-off-duty/deactivate
 * Deactivates (turns off) the all-members-off-duty toggle.
 */
export const deactivateAllMembersOffDuty =
  (body: SustainAllMembersOffDutyDeactivateRequest) =>
  async (_dispatch: AppDispatch): Promise<SustainAllMembersOffDutyResponse> => {
    const api = createApiClient();
    const resp = await api.post<{ success: boolean; message: string; data: SustainAllMembersOffDutyResponse }>(
      "/ticketing/sustain-team/all-members-off-duty/deactivate",
      body,
    );
    if (!resp.data.success) {
      throw new Error(resp.data.message || "Deactivation failed");
    }
    return resp.data.data;
  };

export const fetchActiveNotifications = () => async (dispatch: AppDispatch): Promise<void> => {
  try {
    const api = createApiClient();
    const response = await api.get<{
      success: boolean;
      data: {
        content: TicketingNotificationApiItem[];
        activeCount?: number;
      };
    }>("/ticketing/notifications?size=100&sortBy=createdAt&sortDir=desc");

    const content = response.data?.data?.content ?? [];
    const apiActiveCount = response.data?.data?.activeCount;
    const fallbackActiveCount = content.filter((n) => n.active === true).length;
    const activeCount = typeof apiActiveCount === "number" ? apiActiveCount : fallbackActiveCount;

    dispatch(
      setNotificationsApiCache({
        content,
        activeCount,
      })
    );
    
    // Filter to only notifications marked to display as homepage banners
    // OR notifications that should display until closed (regardless of banner flag)
    const bannerNotifications = content.filter((n) => 
      n.triggerBannerNotification === true || n.displayUntilClosed === true
    );

    logger.log(`[fetchActiveNotifications] Loaded ${bannerNotifications.length} banner notifications`);

    // Map API notifications to HomepageBannerNotif format and dispatch update
    const banners = bannerNotifications.map((n) => ({
      id: String(n.notificationId),
      // Use NOTIF_TYPE_MAP to correctly map API type strings to frontend types
      type: NOTIF_TYPE_MAP[n.type] ?? "general",
      title: n.title,
      message: n.message ?? n.description ?? "",
      linkedTicket: n.relatedTicketId ?? n.ticketNumber,
      includeActionButton: n.includeActionButton ?? false,
      // Convert endDateTime to "YYYY-MM-DDTHH:mm" format for component filtering
      endDate: toDatetimeLocal(n.endDateTime),
      // Include displayUntilClosed and active flags for visibility control
      displayUntilClosed: n.displayUntilClosed ?? false,
      active: n.active ?? true,
      // Include targeting fields for audience filtering
      allUsers: n.allUsers ?? false,
      targetValues: n.targetValues,
    }));

    dispatch(setHomepageBanners(banners));
    logger.log(
      `[fetchActiveNotifications] Cached ${content.length} notifications, activeCount=${activeCount}, homepageBanners=${banners.length}`
    );
  } catch (err: any) {
    logger.warn("[fetchActiveNotifications] Failed:", err?.message);
    // Non-fatal — homepage displays without banners if API fails
  }
};
